// /src/pages/trackers/ProspectTracker.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  setDoc,
  getDoc,
  addDoc,
  serverTimestamp,
  Timestamp,
  limit,
  orderBy,
  startAfter,
  getDocs,
} from "firebase/firestore";
import { db, storage, auth, googleProvider } from "../../firebase";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { addProspect, inviteProspect } from "./services/prospectWorkflow.js";
import { sendPublicInsightCenterInvite, sendAgentWelcomeEmail } from "../../services/emailService";
import "../../services/emailDebug"; // Load email debug utilities
import {
  addNote,
  deleteNote as deleteNoteService,
  subscribeToProspectNotes,
  getAllNotes,
  subscribeToAllNotes,
} from "../../services/notesService";

import TrackerShell from "../../components/tracker/TrackerShell";
import { ACCOUNT_TYPES, useAccountType } from "../../hooks/useAccountType";

/* 🆕 Profile Card imports */
import UserProfileCard from "../../components/profile/UserProfileCard";
import useProfileModalRoute from "../../components/profile/useProfileModalRoute";

/* Tooltip imports */
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TrackerTooltip,
} from "../../components/ui/tooltip";
import "../../components/ui/tooltip-styles.css";

/* Info tooltip modal - for column info */
const InfoTooltipModal = ({ open, onClose, title, content }) => {
  if (!open) return null;
  return (
    <div className="info-modal-backdrop" onClick={onClose}>
      <div className="info-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="info-modal-header">
          <h4>{title}</h4>
          <button className="info-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="info-modal-body">{content}</div>
      </div>
    </div>
  );
};

/* ===============================
   Service Imports
   =============================== */

/**
 * Find the nearest SMD by traversing up the hierarchy through recruiters
 * @param {string} recruiterId - The ID of the recruiter to start from
 * @param {Array} allUsers - Array of all users with their data
 * @returns {{smdId: string, smdName: string} | null}
 */
function findNearestSMD(recruiterId, allUsers) {
  if (!recruiterId || !allUsers || allUsers.length === 0) return null;

  const visited = new Set(); // Prevent infinite loops
  let currentId = recruiterId;
  const maxDepth = 10; // Safety limit
  let depth = 0;

  while (currentId && depth < maxDepth) {
    if (visited.has(currentId)) {
      console.warn("Circular reference detected in recruiter hierarchy");
      break;
    }
    visited.add(currentId);

    // Find the user in the allUsers array
    const user = allUsers.find(u => u.id === currentId);
    
    if (!user) {
      console.warn(`User ${currentId} not found in hierarchy`);
      break;
    }

    // Check if this user is an SMD
    if (user.level === "SMD") {
      return {
        smdId: currentId,
        smdName: user.name || user.email || currentId,
      };
    }

    // Move up to the next recruiter
    currentId = user.recruiterId;
    depth++;
  }

  return null;
}

/**
 * Find the nearest Leader/Broker/Senior Broker by traversing up the hierarchy
 * @param {string} recruiterId - The ID of the recruiter to start from
 * @param {Array} allUsers - Array of all users with their data
 * @returns {{leaderId: string, leaderName: string} | null}
 */
function findNearestLeader(recruiterId, allUsers) {
  if (!recruiterId || !allUsers || allUsers.length === 0) return null;

  const LEADER_PLANS = new Set([
    ACCOUNT_TYPES.LEADER,
    ACCOUNT_TYPES.BROKER,
    ACCOUNT_TYPES.SENIOR_BROKER,
  ]);

  const visited = new Set();
  let currentId = recruiterId;
  const maxDepth = 10;
  let depth = 0;

  while (currentId && depth < maxDepth) {
    if (visited.has(currentId)) {
      console.warn("Circular reference in hierarchy");
      break;
    }
    visited.add(currentId);

    const user = allUsers.find(u => u.id === currentId);
    
    if (!user) {
      console.warn(`User not found: ${currentId}`);
      break;
    }

    // Check if this user is a Leader, Broker, or Senior Broker
    if (LEADER_PLANS.has(user.plan)) {
      return {
        leaderId: user.id,
        leaderName: user.name || user.email || user.id,
      };
    }

    // Move up to the next recruiter
    currentId = user.recruiterId;
    depth++;
  }

  return null;
}

/* ===============================
   Firestore Refs (UPDATED for 3-collection workflow)
   =============================== */
const prospectsRef = (id) => doc(db, "prospects", id);
const usersRef = (id) => doc(db, "users", id);
const trackersRef = (id) => doc(db, "trackers", id);

/* ===============================
   Helpers
   =============================== */
function uniqueId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  return `id_${Date.now()}_${Math.round(Math.random() * 1e9)}`;
}
function tsToMs(v) {
  if (!v) return 0;
  if (typeof v?.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
}
function formatDateTimeISO(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} ${time}`;
}
function formatDateTimePretty(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const time = d
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    .toLowerCase();
  return `${date} at ${time}`;
}
const calculateAge = (birthday) => {
  if (!birthday) return null;
  try {
    const birthDate = new Date(birthday);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 0 ? age : null;
  } catch (e) {
    return null;
  }
};
function initialsFrom(name) {
  const n = (name || "").trim();
  if (!n) return "?";
  const parts = n.split(/\s+/);
  const letters =
    (parts[0]?.[0] || "") +
    (parts.length > 1 ? parts[parts.length - 1]?.[0] || "" : "");
  return letters.toUpperCase();
}

// Build the list of recruiterIds a user can view based on plan and hierarchy
const DOWNLINE_PLANS = new Set([
  ACCOUNT_TYPES.LEADER,
  ACCOUNT_TYPES.BROKER,
  ACCOUNT_TYPES.SENIOR_BROKER,
  ACCOUNT_TYPES.ADMIN,
]);

function buildAccessibleRecruiters(plan, allUsers, currentUserId) {
  if (!currentUserId) return new Set();

  // New Agent / Agent: only their own prospects
  if (!DOWNLINE_PLANS.has(plan)) {
    return new Set([currentUserId]);
  }

  // Admin: can monitor everything
  if (plan === ACCOUNT_TYPES.ADMIN) {
    const all = new Set([currentUserId]);
    allUsers.forEach((u) => all.add(u.id));
    return all;
  }

  // Leaders/Brokers/Senior Brokers: their prospects + full downline (via recruiterId chain)
  const childrenMap = new Map();
  allUsers.forEach((u) => {
    if (!u?.recruiterId) return;
    if (!childrenMap.has(u.recruiterId)) childrenMap.set(u.recruiterId, []);
    childrenMap.get(u.recruiterId).push(u.id);
  });

  const allowed = new Set([currentUserId]);
  const stack = [currentUserId];
  while (stack.length) {
    const node = stack.pop();
    const kids = childrenMap.get(node) || [];
    kids.forEach((kid) => {
      if (!allowed.has(kid)) {
        allowed.add(kid);
        stack.push(kid);
      }
    });
  }

  return allowed;
}

/* ===============================
   Lock State Management & Column Width Persistence
   =============================== */
const LOCK_STORAGE_KEY = "prospect-tracker:lockedColumns:v1";
const WIDTH_STORAGE_KEY = "prospect-tracker:columnWidths:v1";

const useLockedColumns = () => {
  const [lockedKeys, setLockedKeys] = useState(new Set());

  useEffect(() => {
    // Load persisted lock state from localStorage
    try {
      const saved = localStorage.getItem(LOCK_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setLockedKeys(new Set(parsed));
        }
      }
    } catch (error) {
      console.warn("Failed to load locked columns from localStorage:", error);
    }
  }, []);

  const toggleLock = useCallback((key) => {
    setLockedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      // Persist to localStorage
      try {
        localStorage.setItem(LOCK_STORAGE_KEY, JSON.stringify([...next]));
      } catch (error) {
        console.warn("Failed to save locked columns to localStorage:", error);
      }

      return next;
    });
  }, []);

  return { lockedKeys, toggleLock };
};

const usePersistedColumnWidths = (columns) => {
  const initialWidths = useMemo(() => {
    // Try to load from localStorage first
    try {
      const saved = localStorage.getItem(WIDTH_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge saved widths with default widths
        const widths = {};
        columns.forEach((c) => {
          widths[c.key] = parsed[c.key] || c.width || 140;
        });
        return widths;
      }
    } catch (error) {
      console.warn("Failed to load column widths from localStorage:", error);
    }
    // Fallback to default widths
    return Object.fromEntries(columns.map((c) => [c.key, c.width || 140]));
  }, [columns]);

  const [colWidths, setColWidths] = useState(initialWidths);

  // Save to localStorage whenever widths change
  useEffect(() => {
    try {
      localStorage.setItem(WIDTH_STORAGE_KEY, JSON.stringify(colWidths));
    } catch (error) {
      console.warn("Failed to save column widths to localStorage:", error);
    }
  }, [colWidths]);

  return [colWidths, setColWidths];
};

/* ===============================
   ResizableGrid Component
   =============================== */
function ResizableGrid({
  columns,
  rows,
  loading,
  emptyText,
  renderCell,
  rowKey,
  lockableLeftCount = 3,
  rowStyle,
  sortKey,
  sortDir,
  onSort,
  renderHeaderSearch,
  onScroll,
  loadingMore,
}) {
  const [colWidths, setColWidths] = usePersistedColumnWidths(columns);
  const { lockedKeys, toggleLock } = useLockedColumns();
  const [infoModal, setInfoModal] = useState(null); // { title, content }

  useEffect(() => {
    setColWidths((prev) => {
      const next = { ...prev };
      columns.forEach((c) => {
        if (!(c.key in next)) next[c.key] = c.width || 140;
      });
      return next;
    });
  }, [columns]);

  // Calculate left offsets for locked columns in the correct order
  const leftOffsets = useMemo(() => {
    let acc = 0;
    const offsets = {};

    // Define the fixed order for locked columns
    const lockOrder = ["leader", "recruiter", "name"];

    // Process columns in the fixed order
    lockOrder.forEach((key) => {
      if (lockedKeys.has(key)) {
        offsets[key] = acc;
        acc +=
          colWidths[key] ?? (columns.find((c) => c.key === key)?.width || 140);
      }
    });

    return offsets;
  }, [columns, lockedKeys, colWidths]);

  const dragRef = useRef(null);
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const { key, startX, startW } = dragRef.current;
      const dx = e.clientX - startX;
      const next = Math.max(80, startW + dx);
      setColWidths((w) => ({ ...w, [key]: next }));
    };
    const onUp = () => {
      dragRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const startDrag = (key, e) => {
    dragRef.current = { key, startX: e.clientX, startW: colWidths[key] || 140 };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const infoAllowed = useMemo(() => new Set(["top25", "hot", "profile"]), []);

  return (
    <>
      <InfoTooltipModal 
        open={!!infoModal} 
        onClose={() => setInfoModal(null)}
        title={infoModal?.title || ""}
        content={infoModal?.content || ""}
      />
      <div className="rg-wrap">
        <div 
          className="rg-scroll" 
          onScroll={(e) => {
            if (!onScroll) return;
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            // Trigger load more when scrolled to bottom (with 100px threshold)
            if (scrollHeight - scrollTop - clientHeight < 100) {
              onScroll();
            }
          }}
        >
        <div className="rg-row rg-header">
          {columns.map((c, i) => {
            const w = colWidths[c.key];
            const isLocked = lockedKeys.has(c.key);
            const style = {
              width: w,
              minWidth: w,
              maxWidth: w,
              position: isLocked ? "sticky" : "relative",
              left: isLocked ? leftOffsets[c.key] : undefined,
              zIndex: isLocked ? 5 : 1,
            };
            const isSorted = sortKey === c.key;
            const canSort = !!c.sortable;
            const sortLabel = isSorted ? (sortDir === "asc" ? "▲" : "▼") : "↕";

            return (
              <div
                className={`rg-cell rg-head ${isLocked ? "locked" : ""}`}
                key={c.key}
                style={style}
              >
                <div className="rg-head-inner">
                  <div className="rg-head-top">
                    <div className="rg-title-group">
                      <span className="rg-title">{c.label}</span>
                      {infoAllowed.has(c.key) && (
                        <button
                          type="button"
                          className="mini-btn info-btn"
                          aria-label="Column information"
                          onClick={() => setInfoModal({ title: c.label, content: c.info || "No description available." })}
                        >
                          <span className="info-dot" aria-hidden>
                            i
                          </span>
                        </button>
                      )}
                    </div>
                    <div className="rg-actions-group">
                      {/* Lock button - only for Leader, Recruiter, Name columns */}
                      {["leader", "recruiter", "name"].includes(c.key) && (
                        <button
                          type="button"
                          className={`lock-btn ${isLocked ? "on" : ""}`}
                          onClick={() => toggleLock(c.key)}
                          title={isLocked ? "Unpin column" : "Pin column"}
                          aria-pressed={isLocked}
                        >
                          {isLocked ? "🔒" : "🔓"}
                        </button>
                      )}
                      {canSort && (
                        <button
                          type="button"
                          className={`mini-btn sort-btn ${
                            isSorted ? "on" : ""
                          }`}
                          onClick={() => onSort && onSort(c.key)}
                          title={
                            isSorted
                              ? `Sorted ${
                                  sortDir === "asc" ? "ascending" : "descending"
                                }`
                              : "Sort"
                          }
                        >
                          {sortLabel}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="rg-head-search search-wrap">
                    {renderHeaderSearch ? renderHeaderSearch(c) : null}
                  </div>
                </div>
                <span
                  className="rg-grip"
                  onMouseDown={(e) => startDrag(c.key, e)}
                  title="Drag to resize"
                />
              </div>
            );
          })}
        </div>
        {loading ? (
          <div className="rg-empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="rg-empty">{emptyText || "No data"}</div>
        ) : (
          <>
            {rows.map((r, rowIdx) => {
              const vars =
                (typeof rowStyle === "function" ? rowStyle(r) : {}) || {};
              return (
                <div className="rg-row" key={rowKey ? rowKey(r) : rowIdx}>
                  {columns.map((c) => {
                    const w = colWidths[c.key];
                    const isLocked = lockedKeys.has(c.key);
                    const style = {
                      width: w,
                      minWidth: w,
                      maxWidth: w,
                      position: isLocked ? "sticky" : "relative",
                      left: isLocked ? leftOffsets[c.key] : undefined,
                      zIndex: isLocked ? 3 : 1,
                      ...vars,
                    };
                    return (
                      <div
                        className={`rg-cell ${c.center ? "center" : ""} ${
                          isLocked ? "locked" : ""
                        } ${c.key === "profile" ? "profile-cell" : ""}`}
                        key={c.key}
                        style={style}
                      >
                        {renderCell(r, c)}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {loadingMore && rows.length > 0 && (
              <div className="rg-loading-more">Loading more prospects...</div>
            )}
          </>
        )}
      </div>
      <style>{`
        .rg-wrap{border:1px solid #333;border-radius:14px;overflow:hidden;background:#1a1a1f; display:flex; flex-direction:column; height: 72vh;}
        .rg-scroll{overflow:auto; max-width:100%; flex: 1 1 auto}
        .rg-loading-more{padding:20px;text-align:center;color:#999;font-style:italic;}
        .rg-row{display:flex; position:relative}
        .rg-header{background:#252830; border-bottom:1px solid #3a3f4a}
        .rg-cell{
          padding:10px 12px; border-right:1px solid #2a2e37;
          background:var(--row-bg, #1a1a1f);
          box-shadow: 0 0 0 1px var(--row-ring, transparent) inset;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: flex; 
          align-items: center;
        }
        .rg-cell.rg-head {
          align-items: flex-start;
        }
        .rg-cell.profile-cell {
            align-items: flex-start;
        }
        .rg-head.locked{
          background:#252830;
          box-shadow:
            1px 0 0 #3a3f4a,
            10px 0 18px rgba(0,0,0,.45);
        }
        .rg-head{font-weight:700; position:relative;}
        .rg-head-inner{display:flex; flex-direction:column; gap:6px; width: 100%; align-items: stretch;}
        .rg-head-top{width: 100%; display:flex; align-items:flex-start; justify-content: space-between; gap:6px; padding-top: 4px;}
        .rg-title-group { 
          display: flex; 
          align-items: center; 
          justify-content: flex-start; 
          gap: 8px; 
          flex: 1;
          min-width: 0;
        }
        .rg-actions-group { 
          display: flex; 
          align-items: center; 
          justify-content: center;
          gap: 12px; 
          flex-shrink: 0;
          margin-left: 8px;
          position: relative;
          z-index: 1;
          height: 28px;
          line-height: 28px;
          margin-top: 0;
          margin-bottom: 0;
        }
        .rg-title{
          pointer-events:none;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .rg-head-search{display:block; position: relative;}
        .search-wrap {
          position: relative;
        }
        .mini-search{
          width:100%;
          background:rgba(255,255,255,.07);
          color:#fff;
          border:1px solid rgba(255,255,255,.25);
          border-radius:8px;
          padding:6px 8px;
          font-size:12px;
          outline:none;
        }
        .mini-search::placeholder{color:rgba(255,255,255,.65)}
        .rg-cell.center{display:flex; align-items:center; justify-content:center}
        .rg-row:not(.rg-header) .rg-cell{border-top:1px solid #2a2e37}
        .rg-empty{padding:24px; opacity:.8}
        .lock-btn{
          border:none; 
          border-radius:6px; 
          padding:0;
          background:rgba(255,255,255,.06); 
          color:#fff; 
          cursor:pointer; 
          font-size:11px;
          transition: all 0.2s ease;
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          position: relative;
          z-index: 1;
          vertical-align: top;
          line-height: 1;
          margin: 0;
          margin-top: -8px;
          align-self: center;
        }
        .lock-btn.on{
          background:rgba(255,215,0,.22); 
          color:#ffd700; 
          border:1px solid rgba(255,215,0,.4);
        }
        .lock-btn:hover {
          background: rgba(255, 255, 255, 0.12);
        }
        .mini-btn{
          border:none; 
          border-radius:6px; 
          padding:0; 
          font-size:11px; 
          cursor:pointer;
          background:rgba(255,255,255,.06); 
          color:#fff;
          transition: all 0.2s ease;
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          position: relative;
          z-index: 1;
          vertical-align: top;
          line-height: 1;
          margin: 0;
          align-self: center;
        }
        .mini-btn.on{background:rgba(255,255,255,.12)}
        .mini-btn:hover {
          background: rgba(255, 255, 255, 0.12);
        }
        .sort-btn{ color:#bfbfbf; }
        .sort-btn.on{ color:#fff; border:1px solid rgba(255,255,255,.28) }
        /* Tooltip styles moved to tooltip-styles.css */
        .rg-grip{
          position:absolute; top:0; right:-3px; width:6px; height:100%;
          cursor:col-resize; background:transparent; z-index: 10;
        }
        .rg-head:hover .rg-grip{background:linear-gradient(to right, transparent 30%, rgba(255,255,255,.12), transparent 70%)}
        .palette-pop{
          position:fixed; z-index:2000; background:#fff; color:#111;
          border-radius:10px; box-shadow:0 12px 28px rgba(0,0,0,.35);
          border:1px solid rgba(0,0,0,.08); overflow:hidden; min-width:220px;
        }
        .pal-item{display:flex; align-items:center; gap:10px; padding:8px 12px; cursor:pointer; font-size:16px}
        .pal-item:hover{background:#f3f3f3}
        .dot{width:14px; height:14px; border-radius:50%}
        .icon-btn{
          display:inline-flex; align-items:center; justify-content:center;
          width:34px; height:34px; border-radius:8px;
          background:rgba(255,255,255,.08); color:#fff;
          border:1px solid rgba(255,255,255,.25);
          font-size:18px; line-height:1; cursor:pointer;
          flex-shrink: 0;
        }
        .icon-btn:hover{background:rgba(255,255,255,.14)}
        .icon-btn.danger{border-color:rgba(239,83,80,.55)}
        .name-like-cell { display: flex; align-items: center; gap: 8px; width: 100%; overflow: hidden; }
        .name-like-cell .cell-text { flex-grow: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-align: left;}
        .profile-card-btn { width: 28px; height: 28px; font-size: 14px; line-height: 1; padding: 4px;}
        /* Info Modal Styles */
        .info-modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 999999; }
        .info-modal-card { background: #1f2128; color: #fff; border: 1px solid #3a3f4a; border-radius: 16px; box-shadow: 0 14px 40px rgba(0,0,0,0.6); padding: 20px; max-width: 500px; width: 90%; }
        .info-modal-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
        .info-modal-header h4 { margin: 0; font-size: 18px; font-weight: 700; }
        .info-modal-close { background: transparent; border: none; color: #fff; font-size: 24px; cursor: pointer; opacity: 0.8; padding: 0; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; }
        .info-modal-close:hover { opacity: 1; }
        .info-modal-body { font-size: 14px; line-height: 1.6; opacity: 0.95; }
      `}</style>
      </div>
    </>
  );
}

/* ===============================
   Column Definitions
   =============================== */
const COLS = [
  {
    key: "mark",
    label: "",
    type: "marker",
    width: 42,
    center: true,
    showLock: false,
    sortable: false,
    info: "Highlighter tag for the entire row. Click the dot to set Client / Recruit / Both.",
  },
  {
    key: "leader",
    label: "Leader",
    type: "text",
    width: 180,
    sortable: true,
    info: "Name of the team leader responsible for this prospect.",
  },
  {
    key: "recruiter",
    label: "Recruiter",
    type: "text",
    width: 180,
    sortable: true,
    info: "Who owns the relationship and added the prospect.",
  },
  {
    key: "name",
    label: "Name",
    type: "text",
    width: 240,
    sortable: true,
    info: "Prospect's full name.",
  },
  {
    key: "top25",
    label: "Top 25",
    type: "check",
    width: 90,
    center: true,
    sortable: true,
    info: "Active warm Prospect",
  },
  {
    key: "hot",
    label: "HOT",
    type: "check",
    width: 80,
    center: true,
    sortable: true,
    info: "Can be completed before the end of the month",
  },
  {
    key: "outcome",
    label: "Outcome",
    type: "select",
    width: 150,
    sortable: true,
    info: "Primary path you expect for this prospect (Client/Recruit/Both).",
    options: ["Client", "Recruit", "Both"],
  },
  {
    key: "profile",
    label: "Profile",
    type: "profileLine",
    width: 520,
    sortable: false,
    info: "Relationship, Relationship Scale (1-10), Occupation, Married, Dependent Kids, Age. Click to view/edit profile flags.",
  },
  {
    key: "notes",
    label: "Notes",
    type: "notesInline",
    width: 520,
    sortable: true,
    info: "Inline notes or last conversation details.",
  },
  {
    key: "actions",
    label: "Actions",
    type: "actionsInline",
    width: 180,
    sortable: false,
    info: "Edit, call, or delete the row.",
  },
];

/* ===============================
   Modals & Forms
   =============================== */
function RecruiterSearchModal({ open, onClose, onSelect, items, title }) {
  const [query, setQuery] = useState("");
  if (!open) return null;

  // Handle both array of strings and array of objects with name property
  const itemNames = items.map(item => typeof item === 'string' ? item : item.name);
  
  const filteredItems = itemNames.filter((item) =>
    item.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <Modal open={open} onClose={onClose} title={title || "Select Recruiter"}>
      <div className="recruiter-search">
        <input
          type="text"
          className="input"
          placeholder="Search..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        <div className="recruiter-list">
          {filteredItems.map((item) => (
            <div
              key={item}
              className="recruiter-item"
              onClick={() => onSelect(item)}
            >
              {item}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

function ProspectCardModal({
  open,
  onClose,
  onSave,
  defaultRecruiter,
  defaultRecruiterId,
  existingProspect,
  recruiters,
}) {
  const isEditMode = !!existingProspect;
  const [form, setForm] = useState(() =>
    isEditMode ? { ...emptyRow(), ...existingProspect } : emptyRow()
  );
  const [recruiterSearchOpen, setRecruiterSearchOpen] = useState(false);
  const [leaderSearchOpen, setLeaderSearchOpen] = useState(false);
  const [leaders, setLeaders] = useState([]);
  const [leaderSearchQuery, setLeaderSearchQuery] = useState("");

  useEffect(() => {
    if (isEditMode) {
      setForm({ ...emptyRow(), ...existingProspect });
    } else {
      setForm((f) => ({ 
        ...emptyRow(), 
        recruiter: defaultRecruiter || "",
        recruiterId: defaultRecruiterId || ""
      }));
    }
  }, [existingProspect, isEditMode, defaultRecruiter, defaultRecruiterId]);

  // Auto-set recruiterId when recruiter name is present
  useEffect(() => {
    if (form.recruiter && !form.recruiterId && recruiters.length > 0) {
      const recruiterObj = recruiters.find(r => r.name === form.recruiter);
      if (recruiterObj) {
        setForm(f => ({ ...f, recruiterId: recruiterObj.id }));
      }
    }
  }, [form.recruiter, form.recruiterId, recruiters]);

  // Auto-set leaderId when leader name is present
  useEffect(() => {
    if (form.leader && !form.leaderId && leaders.length > 0) {
      const leaderObj = leaders.find(l => l.name === form.leader);
      if (leaderObj) {
        setForm(f => ({ ...f, leaderId: leaderObj.id }));
      }
    }
  }, [form.leader, form.leaderId, leaders]);

  // Fetch leaders from users table
  useEffect(() => {
    if (!open) return;
    const qy = query(
      collection(db, "users"),
      where("plan", "==", "Leader")
    );
    const unsub = onSnapshot(qy, (snap) => {
      const leadersList = snap.docs.map((d) => ({
        id: d.id,
        name: d.data().name || d.data().email || d.id,
        ...d.data()
      }));
      setLeaders(leadersList.sort((a, b) => a.name.localeCompare(b.name)));
    });
    return () => unsub();
  }, [open]);

  const push = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pushP = (k, v) =>
    setForm((f) => ({ ...f, profile: { ...(f.profile || {}), [k]: v } }));
  const pushFlag = (k, v) =>
    setForm((f) => ({
      ...f,
      profile: {
        ...(f.profile || {}),
        flags: { ...(f.profile?.flags || {}), [k]: v },
      },
    }));

  const submit = async () => {
    // Validate at least one unique identifier
    const email = form.email?.trim();
    const phone = form.phone?.trim();
    
    if (!email && !phone) {
      alert('Please provide at least one contact method: Email or Phone');
      return;
    }

    // Ensure recruiterId is set if recruiter name exists
    let finalForm = { ...form };
    if (finalForm.recruiter && !finalForm.recruiterId && recruiters.length > 0) {
      const recruiterObj = recruiters.find(r => r.name === finalForm.recruiter);
      if (recruiterObj) {
        finalForm.recruiterId = recruiterObj.id;
      }
    }
    // Ensure leaderId is set if leader name exists
    if (finalForm.leader && !finalForm.leaderId && leaders.length > 0) {
      const leaderObj = leaders.find(l => l.name === finalForm.leader);
      if (leaderObj) {
        finalForm.leaderId = leaderObj.id;
      }
    }
    await onSave(finalForm);
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={isEditMode ? "Prospect Card" : "Add Prospect"}
        wide
      >
        <div className="add-form">
          {/* Section 1: Basic Info */}
          <div className="stack">
            <div className="subhead">First Name*</div>
            <input
              className="input"
              value={form.firstName}
              onChange={(e) => push("firstName", e.target.value)}
              placeholder="First name"
            />
          </div>
          <div className="stack">
            <div className="subhead">Last Name*</div>
            <input
              className="input"
              value={form.lastName}
              onChange={(e) => push("lastName", e.target.value)}
              placeholder="Last name"
            />
          </div>
          <div className="stack">
            <div className="subhead">Email</div>
            <input
              className="input"
              type="email"
              value={form.email}
              onChange={(e) => push("email", e.target.value)}
              placeholder="email@example.com"
            />
          </div>
          <div className="row two">
            <div className="stack">
              <div className="subhead">Gender</div>
              <select
                className="input"
                value={form.profile.gender}
                onChange={(e) => pushP("gender", e.target.value)}
              >
                {GENDERS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>
            <div className="stack">
              <div className="subhead">Phone</div>
              <input
                className="input"
                type="tel"
                value={form.phone}
                onChange={(e) => push("phone", e.target.value)}
                placeholder="(555) 555-5555"
              />
            </div>
          </div>
          <div className="row two">
            {/* <div className="stack">
              <div className="subhead">Leader</div>
              <div
                className="autocomplete-picker"
                onClick={() => setLeaderSearchOpen(true)}
              >
                <span>{form.leader || "Select leader..."}</span>
                <button className="btn small" type="button">Change</button>
              </div>
            </div> */}
            {/* <div className="stack">
              <div className="subhead">Level</div>
              <select
                className="input"
                value={form.level}
                onChange={(e) => push("level", e.target.value)}
              >
                <option value="TA">TA</option>
                <option value="TL">TL</option>
                <option value="SMD">SMD</option>
                <option value="MD">MD</option>
              </select>
            </div> */}
          </div>
          <div className="stack">
            <div className="subhead">State Located</div>
            <select
              className="input"
              value={form.profile.state}
              onChange={(e) => pushP("state", e.target.value)}
            >
              <option value="">Select State</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="stack">
            <div className="subhead">Birthday</div>
            <input
              className="input"
              type="date"
              value={form.profile.birthday}
              onChange={(e) => pushP("birthday", e.target.value)}
            />
          </div>

          {/* Section 2: Profile Flags */}
          <div className="stack">
            <div className="subhead">Profile</div>
            <div className="flag-grid">
              {[
                ["age25Plus", "25+ Y.O"],
                ["homeowner", "Homeowner"],
                ["solidCareer", "Solid Career Background"],
                ["income75kPlus", "$75k+ Income"],
                ["dissatisfied", "Dissatisfied"],
                ["entrepreneurial", "Entrepreneurial"],
                ["spanishPreferred", "Spanish Speaking Preferred"],
                ["married", "Married"],
                ["dependentKids", "Dependent Kids"],
              ].map(([key, label]) => (
                <label key={key} className="check-wrap" style={{ gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={!!form.profile.flags?.[key]}
                    onChange={(e) => pushFlag(key, e.target.checked)}
                  />
                  <span /> {label}
                </label>
              ))}
            </div>
          </div>

          {/* Section 3: Relationship */}
          <div className="stack">
            <div className="subhead">How do you know this person?</div>
            <input
              className="input"
              value={form.profile.howKnown}
              onChange={(e) => pushP("howKnown", e.target.value)}
            />
          </div>
          <div className="stack">
            <div className="subhead">
              Relationship (scale of 1-10 to being really close)
            </div>
            <input
              className="input"
              type="number"
              min="1"
              max="10"
              value={form.profile.relationship}
              onChange={(e) => pushP("relationship", e.target.value)}
            />
          </div>
          <div className="stack">
            <div className="subhead">Occupation?</div>
            <input
              className="input"
              value={form.profile.occupation}
              onChange={(e) => pushP("occupation", e.target.value)}
            />
          </div>
          <div className="stack">
            <div className="subhead">
              What have you told this person about our company so far?
            </div>
            <input
              className="input"
              value={form.profile.whatTold}
              onChange={(e) => pushP("whatTold", e.target.value)}
            />
          </div>

          {/* Section 4: Internal */}
          <div className="row two">
            <div className="stack">
              <div className="subhead">Recruiter</div>
              <div
                className="recruiter-picker"
                onClick={() => setRecruiterSearchOpen(true)}
              >
                <span>{form.recruiter || "Select..."}</span>
                <button className="btn small">Change</button>
              </div>
            </div>
            {/* <div className="stack">
              <div className="subhead">Leader/Trainer</div>
              <div
                className="recruiter-picker"
                onClick={() => setLeaderSearchOpen(true)}
              >
                <span>{form.leader || "Select..."}</span>
                <button className="btn small">Change</button>
              </div>
            </div> */}
          </div>

          <div
            className="row"
            style={{ justifyContent: "flex-end", gap: 10, marginTop: "20px" }}
          >
            <button className="btn ghost" onClick={onClose}>
              Cancel
            </button>
            <button className="btn primary" onClick={submit}>
              {isEditMode ? "Save Changes" : "Create"}
            </button>
          </div>
        </div>
        <style>{`
          .add-form{display:grid;gap:16px}
          .flag-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:10px 16px;margin-top:4px}
          .recruiter-picker { display: flex; align-items: center; justify-content: space-between; background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.15); border-radius:10px; padding: 4px 4px 4px 12px; cursor: pointer; height: 43px; box-sizing: border-box; }
          .autocomplete-picker { display: flex; align-items: center; justify-content: space-between; background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.15); border-radius:10px; padding: 4px 4px 4px 12px; cursor: pointer; height: 43px; box-sizing: border-box; }
          .autocomplete-picker:hover { background:rgba(255,255,255,.1); }
        `}</style>
      </Modal>
      <RecruiterSearchModal
        open={recruiterSearchOpen}
        onClose={() => setRecruiterSearchOpen(false)}
        onSelect={(name) => {
          const selectedRecruiter = recruiters.find(r => r.name === name);
          push("recruiter", name);
          if (selectedRecruiter) {
            push("recruiterId", selectedRecruiter.id);
          }
          setRecruiterSearchOpen(false);
        }}
        items={recruiters}
      />
      <RecruiterSearchModal
        open={leaderSearchOpen}
        onClose={() => setLeaderSearchOpen(false)}
        onSelect={(name) => {
          const selectedLeader = leaders.find(l => l.name === name);
          push("leader", name);
          if (selectedLeader) {
            push("leaderId", selectedLeader.id);
          }
          setLeaderSearchOpen(false);
        }}
        items={leaders}
        title="Select Leader"
      />
    </>
  );
}

const emptyRow = () => ({
  mark: "default",
  firstName: "",
  lastName: "",
  name: "",
  email: "",
  phone: "",
  leader: "",
  leaderId: "",
  level: "TA",
  recruiter: "",
  recruiterId: "",
  top25: false,
  hot: false,
  outcome: "Both",
  status: "prospect",
  profile: {
    city: "",
    state: "",
    occupation: "",
    gender: "",
    birthday: "",
    howKnown: "",
    relationship: 0,
    whatTold: "",
    photoUrl: "",
    relationshipStatus: "",
    dependentChildren: "",
    flags: {
      age25Plus: false,
      homeowner: false,
      solidCareer: false,
      income75kPlus: false,
      dissatisfied: false,
      entrepreneurial: false,
      spanishPreferred: false,
    },
  },
  // notes field removed - now stored in separate collection
  files: [],
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
});

function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className={`modal-card ${wide ? "modal-card-wide" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h3>{title}</h3>
          <button className="btn ghost" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

function AddAgencyCodeModal({ prospect, onClose, onSubmit }) {
  const [form, setForm] = useState({
    amaDate: new Date().toISOString().split('T')[0],
    agencyCode: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    poloSize: '',
    spouseName: '',
    spousePhone: '',
    spousePoloSize: '',
    recruiter: '',
    recruiterId: '',
    leader: '',
    leaderId: '',
    level: 'TA',
    plan: ACCOUNT_TYPES.NEW_AGENT,
  });

  const [leaderSearchOpen, setLeaderSearchOpen] = useState(false);
  const [leaders, setLeaders] = useState([]);
  const [recruiterSearchOpen, setRecruiterSearchOpen] = useState(false);
  const [recruiters, setRecruiters] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  // Fetch leaders from users collection
  useEffect(() => {
    const qy = query(
      collection(db, "users"),
      where("plan", "in", [ACCOUNT_TYPES.LEADER, ACCOUNT_TYPES.BROKER, ACCOUNT_TYPES.SENIOR_BROKER])
    );
    const unsub = onSnapshot(qy, (snap) => {
      const leadersList = snap.docs.map((d) => ({
        id: d.id,
        name: d.data().name || d.data().email || d.id,
        ...d.data()
      }));
      setLeaders(leadersList.sort((a, b) => a.name.localeCompare(b.name)));
    });
    return () => unsub();
  }, []);

  // Fetch all users for recruiters list and hierarchy traversal
  useEffect(() => {
    const qy = query(collection(db, "users"));
    const unsub = onSnapshot(qy, (snap) => {
      const usersList = snap.docs.map((d) => ({
        id: d.id,
        name: d.data().name || d.data().email || d.id,
        ...d.data()
      }));
      setRecruiters(usersList.sort((a, b) => a.name.localeCompare(b.name)));
      setAllUsers(usersList);
    });
    return () => unsub();
  }, []);

  // Update form when prospect changes - auto-populate leader from hierarchy
  useEffect(() => {
    if (prospect && allUsers.length > 0) {
      const baseFormData = {
        firstName: prospect.firstName || prospect.name?.split(' ')[0] || '',
        lastName: prospect.lastName || prospect.name?.split(' ').slice(1).join(' ') || '',
        phone: prospect.phone || '',
        email: prospect.email || '',
        recruiter: prospect.recruiter || '',
        recruiterId: prospect.recruiterId || '',
        level: prospect.level || 'TA',
      };

      // Auto-populate leader by finding first Leader/Broker/Senior Broker in hierarchy
      let leaderData = { leader: '', leaderId: '' };
      
      if (prospect.recruiterId) {
        const foundLeader = findNearestLeader(prospect.recruiterId, allUsers);
        if (foundLeader) {
          leaderData = {
            leader: foundLeader.leaderName,
            leaderId: foundLeader.leaderId,
          };
        }
      }

      setForm(prevForm => ({
        ...prevForm,
        ...baseFormData,
        ...leaderData,
      }));
    }
  }, [prospect, allUsers]);

  // Auto-set recruiterId when recruiter name is present
  useEffect(() => {
    if (form.recruiter && !form.recruiterId && recruiters.length > 0) {
      const recruiterObj = recruiters.find(r => r.name === form.recruiter);
      if (recruiterObj) {
        setForm(f => ({ ...f, recruiterId: recruiterObj.id }));
      }
    }
  }, [form.recruiter, form.recruiterId, recruiters]);

  // Auto-set leaderId when leader name is present
  useEffect(() => {
    if (form.leader && !form.leaderId && leaders.length > 0) {
      const leaderObj = leaders.find(l => l.name === form.leader);
      if (leaderObj) {
        setForm(f => ({ ...f, leaderId: leaderObj.id }));
      }
    }
  }, [form.leader, form.leaderId, leaders]);

  if (!prospect) return null;

  const handleSubmit = async () => {
    if (!form.agencyCode.trim()) {
      alert('Agency Code is required');
      return;
    }
    // Ensure recruiterId is set if recruiter name exists
    let finalForm = { ...form };
    if (finalForm.recruiter && !finalForm.recruiterId && recruiters.length > 0) {
      const recruiterObj = recruiters.find(r => r.name === finalForm.recruiter);
      if (recruiterObj) {
        finalForm.recruiterId = recruiterObj.id;
      }
    }
    if (finalForm.leader && !finalForm.leaderId && leaders.length > 0) {
      const leaderObj = leaders.find(l => l.name === finalForm.leader);
      if (leaderObj) {
        finalForm.leaderId = leaderObj.id;
      }
    }
    await onSubmit(finalForm);
    onClose();
  };

  const updateField = (key, value) => setForm(f => ({ ...f, [key]: value }));

  return (
    <Modal open={!!prospect} onClose={onClose} title="Add Agent" wide>
      <div className="add-agent-form">
        <div className="row two">
          <div className="stack">
            <div className="subhead">AMA Date*</div>
            <input
              className="input"
              type="date"
              value={form.amaDate}
              onChange={(e) => updateField('amaDate', e.target.value)}
            />
          </div>
          <div className="stack">
            <div className="subhead">Agency Code*</div>
            <input
              className="input"
              value={form.agencyCode}
              onChange={(e) => updateField('agencyCode', e.target.value)}
              placeholder="Enter agency code"
            />
          </div>
        </div>
        
        <div className="row two">
          <div className="stack">
            <div className="subhead">First Name*</div>
            <input
              className="input"
              value={form.firstName}
              onChange={(e) => updateField('firstName', e.target.value)}
              placeholder="First name"
            />
          </div>
          <div className="stack">
            <div className="subhead">Last Name*</div>
            <input
              className="input"
              value={form.lastName}
              onChange={(e) => updateField('lastName', e.target.value)}
              placeholder="Last name"
            />
          </div>
        </div>

        <div className="row two">
          <div className="stack">
            <div className="subhead">Phone*</div>
            <input
              className="input"
              type="tel"
              value={form.phone}
              onChange={(e) => updateField('phone', e.target.value)}
              placeholder="(555) 555-5555"
            />
          </div>
          <div className="stack">
            <div className="subhead">E-mail*</div>
            <input
              className="input"
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="email@example.com"
            />
          </div>
        </div>

        <div className="stack">
          <div className="subhead">Poloshirt Size</div>
          <select
            className="input"
            value={form.poloSize}
            onChange={(e) => updateField('poloSize', e.target.value)}
          >
            <option value="">Select Size</option>
            <option value="Male XS">Male XS</option>
            <option value="Male S">Male S</option>
            <option value="Male M">Male M</option>
            <option value="Male L">Male L</option>
            <option value="Male XL">Male XL</option>
            <option value="Male 2XL">Male 2XL</option>
            <option value="Male 3XL">Male 3XL</option>
            <option value="Female XS">Female XS</option>
            <option value="Female S">Female S</option>
            <option value="Female M">Female M</option>
            <option value="Female L">Female L</option>
            <option value="Female XL">Female XL</option>
            <option value="Female 2XL">Female 2XL</option>
            <option value="Female 3XL">Female 3XL</option>
          </select>
        </div>

        <div className="row two">
          <div className="stack">
            <div className="subhead">Spouse Full Name</div>
            <input
              className="input"
              value={form.spouseName}
              onChange={(e) => updateField('spouseName', e.target.value)}
              placeholder="Spouse name"
            />
          </div>
          <div className="stack">
            <div className="subhead">Spouse Phone</div>
            <input
              className="input"
              type="tel"
              value={form.spousePhone}
              onChange={(e) => updateField('spousePhone', e.target.value)}
              placeholder="(555) 555-5555"
            />
          </div>
        </div>

        <div className="stack">
          <div className="subhead">Spouse Poloshirt Size</div>
          <select
            className="input"
            value={form.spousePoloSize}
            onChange={(e) => updateField('spousePoloSize', e.target.value)}
          >
            <option value="">Select Size</option>
            <option value="Female XS">Female XS</option>
            <option value="Female S">Female S</option>
            <option value="Female M">Female M</option>
            <option value="Female L">Female L</option>
            <option value="Female XL">Female XL</option>
            <option value="Female 2XL">Female 2XL</option>
            <option value="Female 3XL">Female 3XL</option>
            <option value="Male XS">Male XS</option>
            <option value="Male S">Male S</option>
            <option value="Male M">Male M</option>
            <option value="Male L">Male L</option>
            <option value="Male XL">Male XL</option>
            <option value="Male 2XL">Male 2XL</option>
            <option value="Male 3XL">Male 3XL</option>
          </select>
        </div>

        <div className="row two">
          <div className="stack">
            <div className="subhead">Recruiter*</div>
            <div
              className="recruiter-picker input"
              onClick={() => setRecruiterSearchOpen(true)}
            >
              <span style={{marginRight: '8px'}}>{form.recruiter || "Select recruiter..."}</span>
              <button className="btn small" type="button">Change</button>
            </div>
          </div>
          <div className="stack">
            <div className="subhead">Leader/Trainer*</div>
            <div
              className="recruiter-picker input"
              onClick={() => setLeaderSearchOpen(true)}
            >
              <span style={{marginRight: '8px'}}>{form.leader || "Select leader..."}</span>
              <button className="btn small" type="button">Change</button>
            </div>
          </div>
        </div>

        <div className="row two">
          <div className="stack">
            <div className="subhead">Level*</div>
            <select
              className="input"
              value={form.level}
              onChange={(e) => updateField('level', e.target.value)}
            >
              <option value="TA">TA</option>
              <option value="A">A</option>
              <option value="SMD">SMD</option>
              <option value="MD">MD</option>
            </select>
          </div>
          <div className="stack">
            <div className="subhead">Plan*</div>
            <select
              className="input"
              value={form.plan}
              onChange={(e) => updateField('plan', e.target.value)}
            >
              {Object.values(ACCOUNT_TYPES).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row" style={{ justifyContent: 'flex-end', gap: 10, marginTop: '20px' }}>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={handleSubmit}>
            Submit
          </button>
        </div>
      </div>
      <style>{`
        .add-agent-form { display: grid; gap: 16px; }
      `}</style>
      
      {/* Recruiter Search Modal */}
      <RecruiterSearchModal
        open={recruiterSearchOpen}
        onClose={() => setRecruiterSearchOpen(false)}
        onSelect={(name) => {
          const selectedRecruiter = recruiters.find(r => r.name === name);
          updateField('recruiter', name);
          if (selectedRecruiter) {
            updateField('recruiterId', selectedRecruiter.id);
          }
          setRecruiterSearchOpen(false);
        }}
        items={recruiters.map(r => r.name)}
        title="Select Recruiter"
      />
      
      {/* Leader Search Modal */}
      <RecruiterSearchModal
        open={leaderSearchOpen}
        onClose={() => setLeaderSearchOpen(false)}
        onSelect={(name) => {
          updateField('leader', name);
          updateField('leaderId', leaders.find(l => l.name === name)?.id || '');
          setLeaderSearchOpen(false);
        }}
        items={leaders.map(l => l.name)}
        title="Select Leader/Trainer"
      />
    </Modal>
  );
}

/* ===============================
   Add Production Modal
   =============================== */
function AddProductionModal({ open, onClose, prospect, onSave }) {
  const [form, setForm] = useState({
    status: "Active",
    dateWritten: new Date().toISOString().split("T")[0],
    closureDate: "",
    client: "",
    agentMode: "single",
    agent1: "",
    agent1Id: "",
    agent2: "",
    agent2Id: "",
    agent1Pct: 100,
    agent2Pct: 0,
    split: "100/0",
    targetPoints: "",
    company: "",
    product: "",
    otherProduct: "",
    multiplierPercent: "",
    policyNumber: "",
    delivery: "Email",
    trialApp: false,
    notes: "",
  });

  // Autocomplete state: load users and control modals
  const [users, setUsers] = useState([]);
  const [agent1SearchOpen, setAgent1SearchOpen] = useState(false);
  const [agent2SearchOpen, setAgent2SearchOpen] = useState(false);
  const [agentMode, setAgentMode] = useState("single");

  // Constants - defined before functions that use them
  const STATUS_OPTS = ["Active", "Pending", "Completed", "Cancelled"];
  const SPLIT_OPTS = ["50/50", "70/30"];
  const COMPANY_OPTS = ["TRANSAMERICA", "NATIONWIDE", "NORTH AMERICAN", "EVEREST", "OTHER"];
  const DELIVERY_OPTS = ["Email", "Mail", "In Person", "Digital"];

  const PRODUCTS_BY_COMPANY = {
    "TRANSAMERICA": [
      "FFIUL II",
      "TERM LB - 10 YEARS",
      "TERM LB - 15 YEARS",
      "TERM LB - 20/25/30 YEARS",
      "FINAL EXPENSE"
    ],
    "NATIONWIDE": ["NEW HEIGHTS IUL ACCUMULATOR 2020"],
    "NORTH AMERICAN": [
      "SECURE HORIZON - CLIENT AGE 0-70",
      "SECURE HORIZON - CLIENT AGE 71-75",
      "SECURE HORIZON - CLIENT AGE 76+"
    ],
    "EVEREST": ["EVEREST"],
    "OTHER": ["OTHER"]
  };

  const MULTIPLIER_TABLE = {
    "TRANSAMERICA|FFIUL II": 1.25,
    "TRANSAMERICA|TERM LB - 10 YEARS": 1.10,
    "TRANSAMERICA|TERM LB - 15 YEARS": 1.16,
    "TRANSAMERICA|TERM LB - 20/25/30 YEARS": 1.26,
    "TRANSAMERICA|FINAL EXPENSE": 1.10,
    "NATIONWIDE|NEW HEIGHTS IUL ACCUMULATOR 2020": 1.09,
    "NORTH AMERICAN|SECURE HORIZON - CLIENT AGE 0-70": 0.062888,
    "NORTH AMERICAN|SECURE HORIZON - CLIENT AGE 71-75": 0.053496,
    "NORTH AMERICAN|SECURE HORIZON - CLIENT AGE 76+": 0.040919,
    "EVEREST|EVEREST": 1.00
  };

  useEffect(() => {
    if (prospect) {
      setForm((prev) => ({
        ...prev,
        client: prospect.name || "",
        agent1: prospect.recruiter || "",
        agent1Id: prospect.recruiterId || "",
        agent2: prospect.leader || "",
        agent2Id: prospect.leaderId || "",
      }));
    }
  }, [prospect]);

  // Load all users for autocomplete dropdowns
  useEffect(() => {
    if (!open) return;
    const qy = query(collection(db, "users"));
    const unsub = onSnapshot(qy, (snap) => {
      const list = snap.docs
        .map((d) => ({ id: d.id, name: d.data().name || d.data().email || d.id }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setUsers(list);
    });
    return () => unsub();
  }, [open]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAgentModeChange = (mode) => {
    setAgentMode(mode);
    if (mode === "single") {
      setForm(prev => ({
        ...prev,
        agentMode: "single",
        agent1Pct: 100,
        agent2Pct: 0,
        split: "100/0",
      }));
    } else {
      setForm(prev => ({
        ...prev,
        agentMode: "split",
        agent1Pct: 50,
        agent2Pct: 50,
        split: "50/50",
      }));
    }
  };

  const handleSplitChange = (splitValue) => {
    const [p1, p2] = splitValue.split("/").map(v => parseFloat(v) || 0);
    setForm(prev => ({
      ...prev,
      agent1Pct: p1,
      agent2Pct: p2,
      split: splitValue,
    }));
  };

  const parseSplit = (s) => {
    const [a, b] = (s || "100/0").split("/").map((v) => parseFloat(v) || 0);
    return [a, b];
  };

  const getMultiplier = () => {
    const company = form.company || "";
    const product = form.product || "";
    if (company === "OTHER" || product === "OTHER") {
      const pct = parseFloat(form.multiplierPercent);
      return isNaN(pct) ? 1 : pct;
    }
    const key = `${company}|${product}`;
    return MULTIPLIER_TABLE[key] ?? 1;
  };

  const calculatePoints = () => {
    const base = parseFloat(form.targetPoints) || 0;
    const multiplier = getMultiplier();
    const totalPoints = Math.round(base * multiplier * 100) / 100;
    const [pA, pB] = parseSplit(form.split);

    const pointsA = Math.round(totalPoints * (pA / 100) * 100) / 100;
    const pointsB = Math.round(totalPoints * (pB / 100) * 100) / 100;

    return {
      totalPoints,
      labelA: `${pA}% Points`,
      labelB: `${pB}% Points`,
      pointsA,
      pointsB,
    };
  };

  const handleSubmit = async () => {
    if (!form.client || !form.dateWritten || !form.targetPoints) {
      alert(
        "Please fill in all required fields: Client, Date Written, and Target Points"
      );
      return;
    }

    try {
      const points = calculatePoints();
      await onSave({ ...form, ...points });
      onClose();
    } catch (error) {
      console.error("Failed to save production:", error);
      alert(`Failed to save production: ${error.message}`);
    }
  };

  const points = calculatePoints();

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title="Add Production" wide>
      <div className="production-form">
        <div className="form-section">
          <h4>Basic Info</h4>
          <div className="form-grid">
            <div className="form-field">
              <label className="required">Status</label>
              <select
                className="input"
                value={form.status}
                onChange={(e) => handleChange("status", e.target.value)}
              >
                {STATUS_OPTS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label className="required">Date Written</label>
              <input
                type="date"
                className="input"
                value={form.dateWritten}
                onChange={(e) => handleChange("dateWritten", e.target.value)}
              />
            </div>

            <div className="form-field">
              <label>Closure Date</label>
              <input
                type="date"
                className="input"
                value={form.closureDate}
                onChange={(e) => handleChange("closureDate", e.target.value)}
              />
            </div>

            <div className="form-field full-width">
              <label className="required">Client</label>
              <input
                type="text"
                className="input"
                value={form.client}
                onChange={(e) => handleChange("client", e.target.value)}
                placeholder="Client name"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4>Agents & Share</h4>
          <div className="form-grid">
            <div className="form-field full-width">
              <label>Agent Configuration</label>
              <div style={{ display: "flex", gap: "16px", marginTop: "6px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="agent-mode"
                    value="single"
                    checked={form.agentMode === "single"}
                    onChange={() => handleAgentModeChange("single")}
                  />
                  <span>Single Agent (100%)</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="agent-mode"
                    value="split"
                    checked={form.agentMode === "split"}
                    onChange={() => handleAgentModeChange("split")}
                  />
                  <span>Split Agents</span>
                </label>
              </div>
            </div>

            <div className="form-field">
              <label>Agent 1</label>
              <div
                className="agent-picker input"
                onClick={() => setAgent1SearchOpen(true)}
                role="button"
                tabIndex={0}
              >
                <span style={{ marginRight: 8 }}>{form.agent1 || "Select agent..."}</span>
                <button className="btn small" type="button">Change</button>
              </div>
            </div>

            {form.agentMode === "split" && (
              <div className="form-field">
                <label>Agent 2</label>
                <div
                  className="agent-picker input"
                  onClick={() => setAgent2SearchOpen(true)}
                  role="button"
                  tabIndex={0}
                >
                  <span style={{ marginRight: 8 }}>{form.agent2 || "Select agent..."}</span>
                  <button className="btn small" type="button">Change</button>
                </div>
              </div>
            )}

            {form.agentMode === "split" && (
              <div className="form-field">
                <label>Split</label>
                <select
                  className="input"
                  value={form.split}
                  onChange={(e) => handleSplitChange(e.target.value)}
                >
                  {SPLIT_OPTS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <h4>Policy Details</h4>
          <div className="form-grid">
            <div className="form-field">
              <label>Company</label>
              <select
                className="input"
                value={form.company}
                onChange={(e) => {
                  const val = e.target.value;
                  setForm(prev => ({ ...prev, company: val, product: "", otherProduct: "", multiplierPercent: "" }));
                }}
              >
                <option value="">Select company</option>
                {COMPANY_OPTS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Product</label>
              <select
                className="input"
                value={form.product}
                onChange={(e) => handleChange("product", e.target.value)}
              >
                <option value="">Select product</option>
                {(PRODUCTS_BY_COMPANY[form.company] || []).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            {form.company && form.product && (
              <div className="form-field">
                <label>Multiplier</label>
                <input
                  type="text"
                  className="input"
                  value={getMultiplier()}
                  readOnly
                  disabled
                  style={{ backgroundColor: "rgba(255,255,255,.03)" }}
                />
              </div>
            )}

            {(form.product === "OTHER" || form.company === "OTHER") && (
              <div className="form-field">
                <label>Other Product</label>
                <input
                  type="text"
                  className="input"
                  value={form.otherProduct}
                  onChange={(e) => handleChange("otherProduct", e.target.value)}
                  placeholder="Specify product"
                />
              </div>
            )}

            {(form.product === "OTHER" || form.company === "OTHER") && (
              <div className="form-field">
                <label>Multiplier</label>
                <input
                  type="number"
                  step="0.01"
                  className="input"
                  value={form.multiplierPercent || ""}
                  onChange={(e) => handleChange("multiplierPercent", e.target.value)}
                  placeholder="Enter multiplier (e.g., 1.25 for 125%)"
                />
              </div>
            )}

            <div className="form-field">
              <label>Policy #</label>
              <input
                type="text"
                className="input"
                value={form.policyNumber}
                onChange={(e) => handleChange("policyNumber", e.target.value)}
                placeholder="Policy number"
              />
            </div>

            <div className="form-field">
              <label>Delivery</label>
              <select
                className="input"
                value={form.delivery}
                onChange={(e) => handleChange("delivery", e.target.value)}
              >
                {DELIVERY_OPTS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label className="check-wrap">
                <input
                  type="checkbox"
                  checked={form.trialApp}
                  onChange={(e) => handleChange("trialApp", e.target.checked)}
                />
                <span /> Trial App
              </label>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4>Points Calculation</h4>
          <div className="form-grid">

            <div className="form-field">
              <label className="required">Target Amount</label>
              <input
                type="number"
                className="input"
                value={form.targetPoints}
                onChange={(e) => handleChange("targetPoints", e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="form-field">
              <label>Total Points</label>
              <input
                type="text"
                className="input"
                value={points.totalPoints}
                readOnly
                disabled
              />
            </div>
            <div className="form-field">
              <label>{points.labelA}</label>
              <input
                type="text"
                className="input"
                value={points.pointsA}
                readOnly
                disabled
              />
            </div>
            <div className="form-field">
              <label>{points.labelB}</label>
              <input
                type="text"
                className="input"
                value={points.pointsB}
                readOnly
                disabled
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="form-field full-width">
            <label>Notes</label>
            <textarea
              className="input"
              rows="3"
              value={form.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Additional notes"
            />
          </div>
        </div>

        <div className="form-actions">
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={handleSubmit}>
            Add to Production
          </button>
        </div>
      </div>
      <style>{`
        .production-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .form-section {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .form-section h4 {
          margin: 0;
          color: #ffd700;
          font-size: 14px;
          font-weight: 600;
        }
        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .form-field.full-width {
          grid-column: 1 / -1;
        }
        .form-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .form-field label {
          font-weight: 600;
          opacity: 0.9;
        }
        .form-field label.required::after {
          content: " *";
          color: #ff4444;
        }
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 16px;
        }
        .agent-picker { 
          display: flex; 
          align-items: center; 
          justify-content: space-between; 
          background: rgba(255,255,255,.06); 
          border: 1px solid rgba(255,255,255,.15); 
          border-radius: 10px; 
          padding: 4px 4px 4px 12px; 
          cursor: pointer; 
          height: 43px; 
          box-sizing: border-box; 
        }
      `}</style>

      {/* Agent 1 Search Modal */}
      <RecruiterSearchModal
        open={agent1SearchOpen}
        onClose={() => setAgent1SearchOpen(false)}
        onSelect={(name) => {
          const sel = users.find(u => (typeof u === 'string' ? u : u.name) === name);
          setForm(prev => ({
            ...prev,
            agent1: name,
            agent1Id: sel && typeof sel !== 'string' ? sel.id : "",
          }));
          setAgent1SearchOpen(false);
        }}
        items={users}
        title="Select Agent 1"
      />

      {/* Agent 2 Search Modal */}
      <RecruiterSearchModal
        open={agent2SearchOpen}
        onClose={() => setAgent2SearchOpen(false)}
        onSelect={(name) => {
          const sel = users.find(u => (typeof u === 'string' ? u : u.name) === name);
          setForm(prev => ({
            ...prev,
            agent2: name,
            agent2Id: sel && typeof sel !== 'string' ? sel.id : "",
          }));
          setAgent2SearchOpen(false);
        }}
        items={users}
        title="Select Agent 2"
      />
      </Modal>
  );
}

/* ===============================
   Add Appointment Modal
   =============================== */
function AddAppointmentModal({ open, onClose, prospect, onSave }) {
  const [form, setForm] = useState({
    title: "",
    date: "",
    time: "",
    duration: "30",
    location: "",
    meetingType: "URL",
    notes: "",
  });

  useEffect(() => {
    if (prospect) {
      setForm((prev) => ({
        ...prev,
        title: `${prospect.name} – Appointment`,
        date: new Date().toISOString().split("T")[0],
      }));
    }
  }, [prospect]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.title || !form.date || !form.time) {
      alert("Please fill in all required fields: Title, Date, and Time");
      return;
    }

    try {
      await onSave(form);
      onClose();
    } catch (error) {
      console.error("Failed to save appointment:", error);
      alert(`Failed to save appointment: ${error.message}`);
    }
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title="Add Appointment">
      <div className="appointment-form">
        <div className="form-grid">
          <div className="form-field full-width">
            <label className="required">Title</label>
            <input
              type="text"
              className="input"
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              placeholder="Appointment title"
            />
          </div>

          <div className="form-field">
            <label className="required">Date</label>
            <input
              type="date"
              className="input"
              value={form.date}
              onChange={(e) => handleChange("date", e.target.value)}
            />
          </div>

          <div className="form-field">
            <label className="required">Time</label>
            <input
              type="time"
              className="input"
              value={form.time}
              onChange={(e) => handleChange("time", e.target.value)}
            />
          </div>

          <div className="form-field">
            <label>Duration (min)</label>
            <select
              className="input"
              value={form.duration}
              onChange={(e) => handleChange("duration", e.target.value)}
            >
              <option value="15">15 min</option>
              <option value="30">30 min</option>
              <option value="45">45 min</option>
              <option value="60">60 min</option>
              <option value="90">90 min</option>
              <option value="120">120 min</option>
            </select>
          </div>

          <div className="form-field">
            <label>Location</label>
            <input
              type="text"
              className="input"
              value={form.location}
              onChange={(e) => handleChange("location", e.target.value)}
              placeholder="Meeting location"
            />
          </div>

          <div className="form-field">
            <label>Meeting Type</label>
            <select
              className="input"
              value={form.meetingType}
              onChange={(e) => handleChange("meetingType", e.target.value)}
            >
              <option value="URL">URL</option>
              <option value="Address">Address</option>
            </select>
          </div>

          <div className="form-field full-width">
            <label>Notes</label>
            <textarea
              className="input"
              rows="3"
              value={form.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Additional notes"
            />
          </div>
        </div>

        <div className="form-actions">
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={handleSubmit}>
            Save Appointment
          </button>
        </div>
      </div>
      <style>{`
        .appointment-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .form-field.full-width {
          grid-column: 1 / -1;
        }
        .form-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .form-field label {
          font-weight: 600;
          opacity: 0.9;
        }
        .form-field label.required::after {
          content: " *";
          color: #ff4444;
        }
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 16px;
        }
      `}</style>
    </Modal>
  );
}

/* ===============================
   Trainer Request Modal
   =============================== */
function TrainerRequestModal({ open, onClose, prospect, onSave }) {
  const [form, setForm] = useState({
    timezone: "America/New_York",
    appointmentDate: "",
    appointmentTime: "10:00",
    appointmentType: "Training",
    duration: "60",
    quadrant: "Not Urgent/Important",
    meetingType: "URL",
    meetingUrl: "",
    meetingAddress: "",
    meetingCity: "",
    meetingState: "",
    meetingZip: "",
    notes: "",
    profileFlags: {
      age25Plus: false,
      married: false,
      dependentChildren: false,
      homeowner: false,
      solidCareer: false,
      income75kPlus: false,
      dissatisfied: false,
      entrepreneurial: false,
      spanishPreferred: false,
    }
  });

  useEffect(() => {
    if (prospect) {
      setForm((prev) => ({
        ...prev,
        appointmentDate: new Date().toISOString().split("T")[0],
        notes: '',//`Training request for ${prospect.name}. Contact: ${prospect.email || prospect.phone || "N/A"}`,
      }));
    }
  }, [prospect]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFlagChange = (flag) => {
    setForm((prev) => ({
      ...prev,
      profileFlags: {
        ...prev.profileFlags,
        [flag]: !prev.profileFlags[flag]
      }
    }));
  };

  const handleSubmit = async () => {
    if (!form.appointmentDate || !form.appointmentTime) {
      alert("Please fill in Date and Time");
      return;
    }

    try {
      await onSave(form);
      onClose();
    } catch (error) {
      console.error("Failed to save trainer request:", error);
      alert(`Failed to save trainer request: ${error.message}`);
    }
  };

  if (!open || !prospect) return null;

  return (
    <div className="matchup-modal-overlay" onClick={onClose}>
      <div className="matchup-modal" onClick={(e) => e.stopPropagation()}>
        <div className="matchup-modal-header">
          <h3>Request Trainer - {prospect.name}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#e5e7eb', fontSize: '24px', cursor: 'pointer' }}>✕</button>
        </div>
        <div className="matchup-modal-body">
          {/* Timezone */}
          <div className="form-group">
            <label>Timezone *</label>
            <select 
              className="input"
              value={form.timezone}
              onChange={(e) => handleChange("timezone", e.target.value)}
            >
              <optgroup label="North America">
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Phoenix">Mountain Time - Arizona (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Anchorage">Alaska Time (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii-Aleutian Time (HST)</option>
                <option value="America/Toronto">Eastern Time - Toronto</option>
                <option value="America/Vancouver">Pacific Time - Vancouver</option>
                <option value="America/Mexico_City">Central Time - Mexico City</option>
                <option value="America/Cancun">Eastern Time - Cancun</option>
              </optgroup>
              <optgroup label="South America">
                <option value="America/Sao_Paulo">Brasilia Time (BRT)</option>
                <option value="America/Argentina/Buenos_Aires">Argentina Time (ART)</option>
                <option value="America/Santiago">Chile Time (CLT)</option>
                <option value="America/Lima">Peru Time (PET)</option>
                <option value="America/Bogota">Colombia Time (COT)</option>
                <option value="America/Caracas">Venezuela Time (VET)</option>
              </optgroup>
              <optgroup label="Europe">
                <option value="UTC">UTC</option>
                <option value="Europe/London">Greenwich Mean Time (GMT)</option>
                <option value="Europe/Dublin">Irish Standard Time (IST)</option>
                <option value="Europe/Paris">Central European Time (CET)</option>
                <option value="Europe/Berlin">Central European Time - Berlin</option>
                <option value="Europe/Madrid">Central European Time - Madrid</option>
                <option value="Europe/Rome">Central European Time - Rome</option>
                <option value="Europe/Amsterdam">Central European Time - Amsterdam</option>
                <option value="Europe/Brussels">Central European Time - Brussels</option>
                <option value="Europe/Zurich">Central European Time - Zurich</option>
                <option value="Europe/Vienna">Central European Time - Vienna</option>
                <option value="Europe/Athens">Eastern European Time (EET)</option>
                <option value="Europe/Helsinki">Eastern European Time - Helsinki</option>
                <option value="Europe/Istanbul">Turkey Time (TRT)</option>
                <option value="Europe/Moscow">Moscow Standard Time (MSK)</option>
              </optgroup>
              <optgroup label="Africa">
                <option value="Africa/Cairo">Eastern European Time - Cairo</option>
                <option value="Africa/Johannesburg">South Africa Standard Time (SAST)</option>
                <option value="Africa/Lagos">West Africa Time (WAT)</option>
                <option value="Africa/Nairobi">East Africa Time (EAT)</option>
              </optgroup>
              <optgroup label="Middle East">
                <option value="Asia/Dubai">Gulf Standard Time (GST)</option>
                <option value="Asia/Jerusalem">Israel Standard Time (IST)</option>
                <option value="Asia/Riyadh">Arabia Standard Time (AST)</option>
                <option value="Asia/Tehran">Iran Standard Time (IRST)</option>
              </optgroup>
              <optgroup label="Asia">
                <option value="Asia/Kolkata">India Standard Time (IST)</option>
                <option value="Asia/Karachi">Pakistan Standard Time (PKT)</option>
                <option value="Asia/Dhaka">Bangladesh Standard Time (BST)</option>
                <option value="Asia/Bangkok">Indochina Time (ICT)</option>
                <option value="Asia/Singapore">Singapore Standard Time (SGT)</option>
                <option value="Asia/Hong_Kong">Hong Kong Time (HKT)</option>
                <option value="Asia/Shanghai">China Standard Time (CST)</option>
                <option value="Asia/Taipei">Taipei Standard Time (TST)</option>
                <option value="Asia/Tokyo">Japan Standard Time (JST)</option>
                <option value="Asia/Seoul">Korea Standard Time (KST)</option>
                <option value="Asia/Manila">Philippine Time (PHT)</option>
                <option value="Asia/Jakarta">Western Indonesian Time (WIB)</option>
              </optgroup>
              <optgroup label="Australia & Pacific">
                <option value="Australia/Sydney">Australian Eastern Time (AEST)</option>
                <option value="Australia/Melbourne">Australian Eastern Time - Melbourne</option>
                <option value="Australia/Brisbane">Australian Eastern Time - Brisbane</option>
                <option value="Australia/Adelaide">Australian Central Time (ACST)</option>
                <option value="Australia/Perth">Australian Western Time (AWST)</option>
                <option value="Pacific/Auckland">New Zealand Time (NZST)</option>
                <option value="Pacific/Fiji">Fiji Time (FJT)</option>
                <option value="Pacific/Guam">Chamorro Standard Time (ChST)</option>
              </optgroup>
            </select>
          </div>

          {/* Appointment Date and Time */}
          <div className="form-row">
            <div className="form-group">
              <label>Appointment Date *</label>
              <input 
                type="datetime-local" 
                className="input"
                value={form.appointmentDate ? `${form.appointmentDate}T${form.appointmentTime}` : ""}
                onChange={(e) => {
                  const dt = e.target.value;
                  const [date, time] = dt.split("T");
                  handleChange("appointmentDate", date);
                  if (time) handleChange("appointmentTime", time);
                }}
              />
            </div>
            <div className="form-group">
              <label>Appointment Type</label>
              <select 
                className="input"
                value={form.appointmentType}
                onChange={(e) => handleChange("appointmentType", e.target.value)}
              >
                <option value="Step 1&2">Step 1&2</option>
                <option value="Step 1">Step 1</option>
                <option value="Step 2">Step 2</option>
                <option value="Step 3">Step 3</option>
                <option value="Step 4">Step 4</option>
                <option value="Step 5">Step 5</option>
                <option value="P">P</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Prospect Details */}
          <div className="form-group">
            <label className="section-header">Prospect Details</label>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Friend Name *</label>
              <input 
                type="text" 
                className="input"
                value={prospect.name || ""}
                disabled
              />
            </div>
            <div className="form-group">
              <label>Friend Phone</label>
              <input 
                type="tel" 
                className="input"
                value={prospect.phone || ""}
                disabled
              />
            </div>
          </div>

          <div className="form-group">
            <label>Friend E-mail</label>
            <input 
              type="email" 
              className="input"
              value={prospect.email || ""}
              disabled
            />
          </div>
          {/* Trainee Section */}
          <div className="form-group">
            <label className="section-header">Trainee</label>
          </div>
          
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>👤</span>
              <span>{localStorage.getItem('wb.name') || auth.currentUser?.displayName || 'Current User'}</span>
            </label>
          </div>
          
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>📧</span>
              <span>{auth.currentUser?.email || localStorage.getItem('wb.email') || 'No email available'}</span>
            </label>
          </div>
          {/* Profile Flags */}
          <div className="form-group">
            <label className="section-header">Profile</label>
            <div className="profile-flags-grid">
              <label className="flag-checkbox">
                <input 
                  type="checkbox" 
                  checked={form.profileFlags.age25Plus}
                  onChange={() => handleFlagChange("age25Plus")}
                />
                <span>25+ yrs Old</span>
              </label>
              <label className="flag-checkbox">
                <input 
                  type="checkbox" 
                  checked={form.profileFlags.married}
                  onChange={() => handleFlagChange("married")}
                />
                <span>Married</span>
              </label>
              <label className="flag-checkbox">
                <input 
                  type="checkbox" 
                  checked={form.profileFlags.dependentChildren}
                  onChange={() => handleFlagChange("dependentChildren")}
                />
                <span>Dependent Children</span>
              </label>
              <label className="flag-checkbox">
                <input 
                  type="checkbox" 
                  checked={form.profileFlags.homeowner}
                  onChange={() => handleFlagChange("homeowner")}
                />
                <span>Homeowner</span>
              </label>
              <label className="flag-checkbox">
                <input 
                  type="checkbox" 
                  checked={form.profileFlags.solidCareer}
                  onChange={() => handleFlagChange("solidCareer")}
                />
                <span>Solid Career Background</span>
              </label>
              <label className="flag-checkbox">
                <input 
                  type="checkbox" 
                  checked={form.profileFlags.income75kPlus}
                  onChange={() => handleFlagChange("income75kPlus")}
                />
                <span>$75k+ Income</span>
              </label>
              <label className="flag-checkbox">
                <input 
                  type="checkbox" 
                  checked={form.profileFlags.dissatisfied}
                  onChange={() => handleFlagChange("dissatisfied")}
                />
                <span>Dissatisfied</span>
              </label>
              <label className="flag-checkbox">
                <input 
                  type="checkbox" 
                  checked={form.profileFlags.entrepreneurial}
                  onChange={() => handleFlagChange("entrepreneurial")}
                />
                <span>Entrepreneurial</span>
              </label>
              <label className="flag-checkbox">
                <input 
                  type="checkbox" 
                  checked={form.profileFlags.spanishPreferred}
                  onChange={() => handleFlagChange("spanishPreferred")}
                />
                <span>Spanish Speaking Preferred</span>
              </label>
            </div>
          </div>
          {/* Meeting Details */}
          <div className="form-row">
            <div className="form-group">
              <label>Duration (minutes)</label>
              <select 
                className="input"
                value={form.duration}
                onChange={(e) => handleChange("duration", e.target.value)}
              >
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">60 minutes</option>
                <option value="90">90 minutes</option>
                <option value="120">120 minutes</option>
              </select>
            </div>
            <div className="form-group">
              <label>Meeting Type *</label>
              <select 
                className="input"
                value={form.meetingType}
                onChange={(e) => handleChange("meetingType", e.target.value)}
              >
                <option value="URL">URL</option>
                <option value="Address">Address</option>
              </select>
            </div>
          </div>

          {/* Meeting Location - URL */}
          {form.meetingType === 'URL' && (
            <div className="form-group">
              <label>Meeting URL *</label>
              <input 
                type="url" 
                className="input"
                value={form.meetingUrl}
                onChange={(e) => handleChange("meetingUrl", e.target.value)}
                placeholder="https://example.com/meeting"
              />
            </div>
          )}

          {/* Meeting Location - Address */}
          {form.meetingType === 'Address' && (
            <>
              <div className="form-group">
                <label>Address *</label>
                <input 
                  type="text" 
                  className="input"
                  value={form.meetingAddress}
                  onChange={(e) => handleChange("meetingAddress", e.target.value)}
                  placeholder="Street address"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City *</label>
                  <input 
                    type="text" 
                    className="input"
                    value={form.meetingCity}
                    onChange={(e) => handleChange("meetingCity", e.target.value)}
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <label>State *</label>
                  <input 
                    type="text" 
                    className="input"
                    value={form.meetingState}
                    onChange={(e) => handleChange("meetingState", e.target.value)}
                    placeholder="State"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Zip *</label>
                <input 
                  type="text" 
                  className="input"
                  value={form.meetingZip}
                  onChange={(e) => handleChange("meetingZip", e.target.value)}
                  placeholder="Zip code"
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label>Choose Quadrant</label>
            <select 
              className="input"
              value={form.quadrant}
              onChange={(e) => handleChange("quadrant", e.target.value)}
            >
              <option value="Urgent/Important">Urgent/Important</option>
              <option value="Not Urgent/Important">Not Urgent/Important</option>
              <option value="Urgent/Not Important">Urgent/Not Important</option>
              <option value="Not Urgent/Not Important">Not Urgent/Not Important</option>
            </select>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label>Notes *</label>
            <textarea 
              className="input"
              rows="3"
              value={form.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Add any notes about the training request..."
              required
            />
          </div>
        </div>
        <div className="matchup-modal-footer">
          <button 
            className="btn-secondary" 
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="btn-primary" 
            onClick={handleSubmit}
          >
            Request Trainer
          </button>
        </div>
      </div>
      <style>{`
        .profile-flags-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
          margin-top: 8px;
        }
        .flag-checkbox {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 6px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.2s;
        }
        .flag-checkbox:hover {
          background: rgba(255, 255, 255, 0.08);
        }
        .flag-checkbox input {
          cursor: pointer;
          accent-color: #3b82f6;
        }
        .section-header {
          font-weight: 600;
          font-size: 14px;
          text-transform: uppercase;
          opacity: 0.9;
          display: block;
          margin-bottom: 8px;
        }
      `}</style>
    </div>
  );
}

function CallLogModal({ prospect, onSaveLog, onClose, onInvite, onAddAgencyCode, onRequestTrainer, onAddAppointment, onAddProduction, isAlreadyInvited, hideRestrictedActions = false, hideAddAgencyCode = false }) {
  const [outcome, setOutcome] = useState("Left Message");
  const [note, setNote] = useState("");

  if (!prospect) return null;

  const handleSave = () => {
    onSaveLog({ outcome, note });
    onClose();
  };

  const callOutcomes = [
    "Left Message",
    "Connected",
    "No Answer",
    "Wrong Number",
    "Follow-up Scheduled",
  ];

  return (
    <Modal
      open={!!prospect}
      onClose={onClose}
      title={`Call Logs: ${prospect.name}`}
    >
      <div className="call-log-form">
        <div className="call-log-input-row">
          <select
            className="input"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
          >
            {callOutcomes.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="input"
            placeholder="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <button className="btn primary" onClick={handleSave}>
            Save
          </button>
        </div>
        <div className="call-log-actions">
          <div className="action-buttons">
            {!hideRestrictedActions && (
              <button className="btn primary" onClick={() => onInvite(prospect)}>
                Invite
              </button>
            )}
            <button className="btn primary" onClick={() => onRequestTrainer(prospect)}>
              Request Trainer
            </button>
            <button className="btn primary" onClick={() => onAddAppointment(prospect)}>
              Add Appointment
            </button>
            {!hideRestrictedActions && (
              <button className="btn primary" onClick={() => onAddProduction(prospect)}>
                Add Production
              </button>
            )}
            {!hideRestrictedActions && !hideAddAgencyCode && (
              <button 
                className="btn primary" 
                onClick={() => onAddAgencyCode(prospect)}
              >
                Add Agency Code {isAlreadyInvited && "(Test Mode)"}
              </button>
            )}
          </div>
          <button className="btn ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      <style>{`
                .call-log-form { display: flex; flex-direction: column; gap: 20px; }
                .call-log-input-row { display: flex; align-items: center; gap: 10px; }
                .call-log-input-row select { flex: 0 0 180px; }
                .call-log-input-row input { flex: 1 1 auto; }
                .call-log-actions { display: flex; justify-content: space-between; align-items: center; }
                .action-buttons { display: flex; gap: 10px; flex-wrap: wrap; }
            `}</style>
    </Modal>
  );
}

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];
const GENDERS = ["", "Male", "Female"];

/* ===============================
   Email Template for Profile Setup
   =============================== */
function generateProfileSetupEmailHTML(name, setupLink, agencyCode, recruiterName) {
  return `
<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <style>
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; line-height: 1.6; color: #333; }
      .container { max-width: 600px; margin: 0 auto; padding: 20px; }
      .header { background: linear-gradient(135deg, #d4af37 0%, #f5d66a 100%); padding: 40px 20px; text-align: center; border-radius: 8px 8px 0 0; }
      .logo { color: #000; font-size: 28px; font-weight: bold; margin: 0; }
      .content { background: #ffffff; padding: 40px 30px; border: 1px solid #e5e5e5; border-top: none; }
      .greeting { font-size: 22px; font-weight: 600; color: #1a1a1a; margin-bottom: 20px; }
      .message { font-size: 16px; color: #4a5568; margin-bottom: 25px; line-height: 1.8; }
      .cta-button { display: inline-block; padding: 16px 40px; background: linear-gradient(135deg, #d4af37 0%, #f5d66a 100%); color: #000; text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 16px; margin: 20px 0; box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3); }
      .info-box { background: #f7fafc; border-left: 4px solid #d4af37; padding: 20px; margin: 25px 0; border-radius: 4px; }
      .footer { background: #1a1a1a; padding: 30px; text-align: center; border-radius: 0 0 8px 8px; }
      .footer-text { font-size: 14px; color: #a0aec0; margin: 5px 0; }
    </style>
  </head>
  <body>
    <div class="container">
      <div class="header">
        <h1 class="logo">💎 WEALTH BUILDERS</h1>
        <p style="color: #000; margin: 10px 0 0 0; font-size: 16px;">Welcome to Your New Journey!</p>
      </div>
      
      <div class="content">
        <div class="greeting">🎊 Welcome, ${name}!</div>
        
        <div class="message">
          <p>Your Wealth Builders account has been created by <strong>${recruiterName}</strong>! Complete your profile by uploading a photo, confirming your name, and setting your password.</p>
          
          <p><strong>Click the button below to get started:</strong></p>
          
          <center>
            <a href="${setupLink}" class="cta-button">
              🚀 Complete My Profile
            </a>
          </center>
          
          ${agencyCode ? `
          <div class="info-box">
            <strong>📋 Your Agency Code:</strong> ${agencyCode}
            <br><small style="color: #718096;">You'll need this for certain features in the platform.</small>
          </div>
          ` : ''}
          
          <p style="margin-top: 30px; font-size: 14px; color: #718096;">
            <strong>⏰ Important:</strong> This link is unique to you. Keep it safe and don't share it with anyone.
          </p>
          
          <p style="margin-top: 20px; font-size: 14px; color: #718096;">
            <em>If the button doesn't work, copy and paste this link into your browser:</em><br>
            <a href="${setupLink}" style="color: #d4af37; word-break: break-all;">${setupLink}</a>
          </p>
        </div>
      </div>
      
      <div class="footer">
        <p class="footer-text" style="color: #d4af37; font-weight: bold; font-size: 16px;">WEALTH BUILDERS</p>
        <p class="footer-text">Building Financial Freedom Together</p>
      </div>
    </div>
  </body>
</html>
  `;
}

/* ===============================
   Main Page Component
   =============================== */
export default function ProspectTracker() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const listenersRef = useRef([]);
  const [prospectNotes, setProspectNotes] = useState({}); // Store notes by prospectId

  // Initialize filters from URL params or localStorage
  const [leaderQ, setLeaderQ] = useState(() => {
    // Check for pending filter from AssociateTracker
    try {
      const pending = localStorage.getItem('prospectTracker:pendingFilter');
      if (pending) {
        const filter = JSON.parse(pending);
        if (filter.type === 'leader' && filter.timestamp > Date.now() - 5000) {
          return filter.value;
        }
      }
    } catch (e) {}
    return searchParams.get("leader") || "";
  });
  
  const [recruiterQ, setRecruiterQ] = useState(() => {
    // Check for pending filter from AssociateTracker
    try {
      const pending = localStorage.getItem('prospectTracker:pendingFilter');
      if (pending) {
        const filter = JSON.parse(pending);
        if (filter.type === 'recruiter' && filter.timestamp > Date.now() - 5000) {
          return filter.value;
        }
      }
    } catch (e) {}
    return searchParams.get("recruiter") || "";
  });
  
  const [nameQ, setNameQ] = useState(searchParams.get("name") || "");

  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  const [addOpen, setAddOpen] = useState(false);
  const [viewingProspect, setViewingProspect] = useState(null);
  const [editingProfileId, setEditingProfileId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  const [notesOpenFor, setNotesOpenFor] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteDeletingId, setNoteDeletingId] = useState(null);
  const [callLogProspect, setCallLogProspect] = useState(null);
  const [focusedNoteInputId, setFocusedNoteInputId] = useState(null);

  const [pal, setPal] = useState(null);
  const palRef = useRef(null);
  useEffect(() => {
    const onDown = (e) => {
      if (!palRef.current) return;
      if (!palRef.current.contains(e.target)) setPal(null);
    };
    const onKey = (e) => e.key === "Escape" && setPal(null);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const [importOpen, setImportOpen] = useState(false);
  const [googleContacts, setGoogleContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [importLoading, setImportLoading] = useState(false);

  const [invitingId, setInvitingId] = useState(null);
  const [addingAgencyCodeFor, setAddingAgencyCodeFor] = useState(null);
  const [addAppointmentFor, setAddAppointmentFor] = useState(null);
  const [addProductionFor, setAddProductionFor] = useState(null);
  const [trainerRequestFor, setTrainerRequestFor] = useState(null);
  const authUser = JSON.parse(localStorage.getItem("authUser") || "null")
  const recruiterName = (
    authUser?.name ||
    authUser?.email ||
    ""
  )
    .toString()
    .trim();

  const recruiterId = auth?.currentUser?.uid || localStorage.getItem("wb.userId") || "";

  const { plan: accountPlan, loading: planLoading } = useAccountType();
  const resolvedPlan = useMemo(
    () => accountPlan || ACCOUNT_TYPES.NEW_AGENT,
    [accountPlan]
  );
  const isNewAgent = resolvedPlan === ACCOUNT_TYPES.NEW_AGENT;
  const isAgent = resolvedPlan === ACCOUNT_TYPES.AGENT;
  const allowAllProspects = resolvedPlan === ACCOUNT_TYPES.ADMIN;
  const [allowedRecruiterIds, setAllowedRecruiterIds] = useState([]);

  // Fetch all users for recruiters dropdown with IDs
  const [allRecruiters, setAllRecruiters] = useState([]);
  useEffect(() => {
    const qy = query(collection(db, "users"));
    const unsub = onSnapshot(qy, (snap) => {
      const recruitersList = snap.docs.map((d) => ({
        id: d.id,
        name: d.data().name || d.data().email || d.id,
        ...d.data()
      }));
      setAllRecruiters(recruitersList.sort((a, b) => a.name.localeCompare(b.name)));
    });
    return () => unsub();
  }, []);

  // Compute which recruiterIds the current user can monitor based on plan and hierarchy
  useEffect(() => {
    if (planLoading) return;
    if (!recruiterId) return;
    if (allRecruiters.length === 0) return; // Wait for users to load

    const allowed = buildAccessibleRecruiters(resolvedPlan, allRecruiters, recruiterId);
    setAllowedRecruiterIds(Array.from(allowed));
  }, [planLoading, resolvedPlan, recruiterId, allRecruiters]);

  // Main prospects query - plan-aware: limit to own prospects unless leader/broker/admin
  useEffect(() => {
    if (planLoading) return;
    if (!recruiterId) return;
    if (!allowAllProspects && allowedRecruiterIds.length === 0) return; // Wait for allowed IDs to compute

    const allowedSet = new Set((allowedRecruiterIds || []).filter(Boolean));
    if (!allowAllProspects && allowedSet.size === 0) return;

    // reset before reload to avoid flashing stale rows when plan changes
    setRows([]);
    setLastDoc(null);
    setHasMore(true);

    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        // If admin, fetch all prospects
        if (allowAllProspects) {
          const qy = query(
            collection(db, "prospects"),
            where("status", "!=", "inactive"),
            orderBy("createdAt", "desc"),
            limit(20)
          );
          const snap = await getDocs(qy);
          const baseRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setRows(baseRows);
          setLastDoc(snap.docs[snap.docs.length - 1]);
          setHasMore(snap.docs.length === 20);
        } else {
          // For non-admins, query by recruiterId in Firestore (not client-side)
          // Firestore 'in' operator supports max 10 values, so batch if needed
          const allowedIds = Array.from(allowedSet);
          
          if (allowedIds.length === 0) {
            setRows([]);
            setHasMore(false);
            return;
          }
          
          // Split into chunks of 10 for Firestore 'in' operator
          const chunks = [];
          for (let i = 0; i < allowedIds.length; i += 10) {
            chunks.push(allowedIds.slice(i, i + 10));
          }
          
          // Fetch from all chunks and combine
          const allDocs = [];
          for (const chunk of chunks) {
            const qy = query(
              collection(db, "prospects"),
              where("status", "!=", "inactive"),
              where("recruiterId", "in", chunk),
              orderBy("createdAt", "desc"),
              limit(20)
            );
            const snap = await getDocs(qy);
            allDocs.push(...snap.docs);
          }
          
          // Sort by createdAt and take top 20
          allDocs.sort((a, b) => {
            const aTime = a.data().createdAt?.seconds || 0;
            const bTime = b.data().createdAt?.seconds || 0;
            return bTime - aTime;
          });
          
          const top20 = allDocs.slice(0, 20);
          const baseRows = top20.map((d) => ({ id: d.id, ...d.data() }));
          
          setRows(baseRows);
          setLastDoc(top20[top20.length - 1]);
          setHasMore(allDocs.length >= 20);
        }
      } catch (error) {
        console.error("Error loading prospects:", error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [planLoading, recruiterId, allowedRecruiterIds, allowAllProspects]);

  // Load more prospects on scroll
  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || !lastDoc) return;

    const allowedSet = new Set((allowedRecruiterIds || []).filter(Boolean));
    if (!allowAllProspects && allowedSet.size === 0) return;
    
    try {
      setLoadingMore(true);
      
      // If admin, fetch all prospects
      if (allowAllProspects) {
        const qy = query(
          collection(db, "prospects"),
          where("status", "!=", "inactive"),
          orderBy("createdAt", "desc"),
          startAfter(lastDoc),
          limit(20)
        );
        const snap = await getDocs(qy);
        const newRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        
        if (newRows.length > 0) {
          setRows(prev => [...prev, ...newRows]);
          setLastDoc(snap.docs[snap.docs.length - 1]);
          setHasMore(snap.docs.length === 20);
        } else {
          setHasMore(false);
        }
      } else {
        // For non-admins, query by recruiterId in Firestore
        const allowedIds = Array.from(allowedSet);
        
        // Split into chunks of 10 for Firestore 'in' operator
        const chunks = [];
        for (let i = 0; i < allowedIds.length; i += 10) {
          chunks.push(allowedIds.slice(i, i + 10));
        }
        
        // Fetch from all chunks and combine
        const allDocs = [];
        for (const chunk of chunks) {
          const qy = query(
            collection(db, "prospects"),
            where("status", "!=", "inactive"),
            where("recruiterId", "in", chunk),
            orderBy("createdAt", "desc"),
            startAfter(lastDoc),
            limit(20)
          );
          const snap = await getDocs(qy);
          allDocs.push(...snap.docs);
        }
        
        // Sort by createdAt
        allDocs.sort((a, b) => {
          const aTime = a.data().createdAt?.seconds || 0;
          const bTime = b.data().createdAt?.seconds || 0;
          return bTime - aTime;
        });
        
        const top20 = allDocs.slice(0, 20);
        const newRows = top20.map((d) => ({ id: d.id, ...d.data() }));
        
        if (newRows.length > 0) {
          setRows(prev => [...prev, ...newRows]);
          setLastDoc(top20[top20.length - 1]);
          setHasMore(allDocs.length >= 20);
        } else {
          setHasMore(false);
        }
      }
    } catch (error) {
      console.error("Error loading more prospects:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, lastDoc, allowedRecruiterIds, allowAllProspects]);

  // Subscribe to all notes for all prospects (by prospectId to include other tracker notes)
  useEffect(() => {
    if (rows.length === 0) return;
    
    console.log('Setting up note subscriptions for prospects (prospectId-based):', rows.length);
    
    const unsubscribers = rows.map(row => {
      if (!row.id) return () => {};

      return subscribeToProspectNotes(row.id, (notes) => {
        console.log(`Received ${notes.length} notes for prospectId ${row.id}`);
        setProspectNotes(prev => ({
          ...prev,
          [row.id]: notes
        }));
      });
    });
    
    return () => {
      console.log('Cleaning up note subscriptions');
      unsubscribers.forEach(unsub => unsub());
    };
  }, [rows.length]); // Only re-subscribe when number of rows changes

  // Debug: Monitor prospectNotes changes
  useEffect(() => {
    console.log('ProspectNotes state updated:', Object.keys(prospectNotes).length, 'prospects with notes');
    Object.entries(prospectNotes).forEach(([prospectId, notes]) => {
      if (notes.length > 0) {
        console.log(`  Prospect ${prospectId}: ${notes.length} notes`);
      }
    });
  }, [prospectNotes]);

  // Debug: Test all notes subscription on mount
  useEffect(() => {
    console.log('[ProspectTracker] Component mounted - testing notes service');
    
    // Test 1: Get all notes once
    getAllNotes().then(allNotes => {
      console.log('[ProspectTracker] getAllNotes test - received:', allNotes.length, 'notes');
    });
    
    // Test 2: Subscribe to all notes
    const unsubAll = subscribeToAllNotes((allNotes) => {
      console.log('[ProspectTracker] subscribeToAllNotes - received:', allNotes.length, 'notes');
    });
    
    return () => {
      unsubAll();
    };
  }, []);

  const setRowLocal = useCallback((rid, updater) => {
    setRows((prev) => prev.map((r) => (r.id === rid ? updater({ ...r }) : r)));
  }, []);

  const profileLine = (p = {}) => {
    if (!p) return "No profile info";
    const age = calculateAge(p.birthday);
    // Order: Relationship, Rel. Scale (1-10), Occupation, Married, Dependent Kids, Age
    const parts = [
      p.howKnown, // Relationship
      p.relationship || p.relationship === 0 ? `${p.relationship}/10` : null, // Rel. Scale (1-10)
      p.occupation, // Occupation
      p.flags?.married ? "Married" : null, // Married
      p.flags?.dependentKids ? "Dependent Kids" : null, // Dependent Kids
      age !== null ? `Age: ${age}` : null, // Age
    ].filter((x) => x !== null && x !== undefined && x !== "");
    return parts.join(" • ") || "No profile info";
  };

  const getSortableValue = (r, key) => {
    switch (key) {
      case "name":
      case "leader":
      case "recruiter":
      case "outcome":
        return (r[key] || "").toString().toLowerCase();
      case "top25":
      case "hot":
        return r[key] ? "yes" : "no";
      case "profile":
        return profileLine(r.profile || "").toLowerCase();
      case "notes":
        const notesArr = prospectNotes[r.id] || [];
        return notesArr
          .map((n) => n.text)
          .join(" ")
          .toLowerCase();
      case "createdAt":
        return r.createdAt?.seconds || 0;
      default:
        return (r[key] ?? "").toString().toLowerCase();
    }
  };

  const filtered = useMemo(() => {
    const lq = leaderQ.trim().toLowerCase();
    const rq = recruiterQ.trim().toLowerCase();
    const nq = nameQ.trim().toLowerCase();
    if (!lq && !rq && !nq) return rows;
    return rows.filter((r) => {
      const okLeader = lq ? (r.leader || "").toLowerCase().includes(lq) : true;
      const okRecruit = rq
        ? (r.recruiter || "").toLowerCase().includes(rq)
        : true;
      const okName = nq ? (r.name || "").toLowerCase().includes(nq) : true;
      return okLeader && okRecruit && okName;
    });
  }, [rows, leaderQ, recruiterQ, nameQ]);

  const effectiveSortKey = nameQ.trim() ? "recruiter" : sortKey;
  const effectiveSortDir = nameQ.trim() ? "asc" : sortDir;

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = getSortableValue(a, effectiveSortKey);
      const vb = getSortableValue(b, effectiveSortKey);
      if (typeof va === "number" && typeof vb === "number") {
        return effectiveSortDir === "asc" ? va - vb : vb - va;
      } else {
        const cmp = String(va).localeCompare(String(vb));
        return effectiveSortDir === "asc" ? cmp : -cmp;
      }
    });
    if (effectiveSortKey !== "name") {
      arr.sort((a, b) => {
        const va = getSortableValue(a, effectiveSortKey);
        const vb = getSortableValue(b, effectiveSortKey);
        if (va === vb) return (a.name || "").localeCompare(b.name || "");
        return 0;
      });
    }
    return arr;
  }, [filtered, effectiveSortKey, effectiveSortDir, getSortableValue]);

  const handleHeaderSort = (colKey) => {
    if (nameQ.trim()) return;
    if (sortKey === colKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(colKey);
      setSortDir("asc");
    }
  };

  const saveProfileUpdate = async () => {
    if (!editingProfileId || !editDraft.profile) return;
    await setDoc(
      prospectsRef(editingProfileId),
      {
        profile: editDraft.profile,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    setEditingProfileId(null);
    setEditDraft({});
  };

  const handleSaveProspect = async (data) => {
    try {
      const email = data.email?.trim();
      const phone = data.phone?.trim();

      // Check if this is an edit (has an id) or a new prospect
      if (data.id) {
        // For edits, check duplicates but exclude current prospect
        if (email || phone) {
          const duplicateQueries = [];
          if (email) {
            duplicateQueries.push(
              getDocs(query(collection(db, "prospects"), where("email", "==", email), where("status", "!=", "inactive")))
            );
          }
          if (phone) {
            duplicateQueries.push(
              getDocs(query(collection(db, "prospects"), where("phone", "==", phone), where("status", "!=", "inactive")))
            );
          }

          const results = await Promise.all(duplicateQueries);
          const duplicates = results.flatMap(snap => snap.docs).filter(doc => doc.id !== data.id);
          
          if (duplicates.length > 0) {
            const dup = duplicates[0].data();
            alert(`A prospect with this ${email && dup.email === email ? 'email' : 'phone'} already exists: ${dup.name || dup.email || dup.phone}`);
            return;
          }
        }

        // Update existing prospect
        await setDoc(
          prospectsRef(data.id),
          {
            firstName: data.firstName?.trim() || "",
            lastName: data.lastName?.trim() || "",
            name: `${data.firstName?.trim() || ""} ${data.lastName?.trim() || ""}`.trim(),
            email: email || "",
            phone: phone || "",
            leader: data.leader?.trim() || "",
            level: data.level?.trim() || "TA",
            recruiter: data.recruiter?.trim() || "",
            recruiterId: data.recruiterId?.trim() || "",
            top25: data.top25 || false,
            hot: data.hot || false,
            outcome: data.outcome || "Both",
            mark: data.mark || "default",
            profile: data.profile || {},
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        console.log("Prospect updated:", data.id);
      } else {
        // For new prospects, check for duplicates
        if (email || phone) {
          const duplicateQueries = [];
          if (email) {
            duplicateQueries.push(
              getDocs(query(collection(db, "prospects"), where("email", "==", email), where("status", "!=", "inactive")))
            );
          }
          if (phone) {
            duplicateQueries.push(
              getDocs(query(collection(db, "prospects"), where("phone", "==", phone), where("status", "!=", "inactive")))
            );
          }

          const results = await Promise.all(duplicateQueries);
          const duplicates = results.flatMap(snap => snap.docs);
          
          if (duplicates.length > 0) {
            const dup = duplicates[0].data();
            alert(`A prospect with this ${email && dup.email === email ? 'email' : 'phone'} already exists: ${dup.name || dup.email || dup.phone}`);
            return;
          }
        }

        // Create new prospect
        const prospectInput = data;
        const { id } = await addProspect(prospectInput);
        console.log("Prospect created with ID:", id);

        // Pull fresh data and inject into the table immediately (no refresh needed)
        const snap = await getDoc(prospectsRef(id));
        if (snap.exists()) {
          const created = { id: snap.id, ...snap.data() };
          const allowedSet = new Set((allowedRecruiterIds || []).filter(Boolean));
          const isVisible = allowAllProspects || allowedSet.has(created.recruiterId);
          if (isVisible) {
            setRows((prev) => {
              const withoutDupes = prev.filter((r) => r.id !== created.id);
              return [created, ...withoutDupes];
            });
          }
        }
      }
    } catch (error) {
      console.error("Error saving prospect:", error);
      alert(`Failed to save prospect: ${error.message}`);
    }
  };

  const removeRow = async (id) => {
    if (!window.confirm("Delete this prospect?")) return;

    // Update prospect status to inactive instead of deleting
    await setDoc(
      prospectsRef(id),
      {
        status: "inactive",
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    // Optimistically remove from local list so it disappears without refresh
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const addNoteInternal = async (rid, baseNotes, text) => {
    // Use centralized notes service
    await addNote({
      prospectId: rid,
      trackerId: rid,
      emailId: rid,
      text: text.trim(),
      createdBy: recruiterName || "Me",
      tracker: "prospect",
    });
  };

  const addInlineNote = async (row) => {
    const text = (noteDrafts[row.id] || "").trim();
    if (!text) return;
    try {
      setNoteSaving(true);
      await addNoteInternal(row.id, [], text);
      setNoteDrafts((d) => ({ ...d, [row.id]: "" }));
    } catch (e) {
      console.error(e);
    } finally {
      setNoteSaving(false);
    }
  };

  const handleSaveCallLog = async ({ outcome, note }) => {
    if (!callLogProspect) return;
    const fullLogText = `Call Log (${outcome}): ${note}`;
    await addNoteInternal(callLogProspect.id, [], fullLogText);
  };

  const deleteNote = async (prospect, noteId) => {
    if (!prospect) return;
    setNoteDeletingId(noteId);
    try {
      await deleteNoteService(noteId);
    } catch (e) {
      console.error(e);
    } finally {
      setNoteDeletingId(null);
    }
  };

  const colorMeta = {
    default: {
      label: "Default",
      dot: "#cfcfcf",
      bg: "#1a1a1f",
      bar: "transparent",
      ring: "transparent",
    },
    client: {
      label: "Client",
      dot: "#00E676",
      bg: "#00b35c",
      bar: "#00E676",
      ring: "#00e6769c",
    },
    recruit: {
      label: "Recruit",
      dot: "#FFB300",
      bg: "#cc8f00",
      bar: "#FFB300",
      ring: "#ffb3009c",
    },
    both: {
      label: "Both",
      dot: "#2979FF",
      bg: "#2366d9",
      bar: "#2979FF",
      ring: "#2979ff9c",
    },
  };
  const rowVars = (r) => {
    const key = (r.mark || "default").toLowerCase();
    const meta = colorMeta[key] || colorMeta.default;
    return {
      "--row-bg": meta.bg,
      "--hl-bar": meta.bar,
      "--row-ring": meta.ring,
    };
  };

  const openPalette = (row, ev) => {
    const rect = ev.currentTarget.getBoundingClientRect();
    setPal({
      row,
      x: rect.left + window.scrollX,
      y: rect.bottom + window.scrollY + 6,
    });
  };
  const setMark = async (row, val) => {
    await setDoc(
      prospectsRef(row.id),
      {
        mark: val,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    setPal(null);
  };

  const handleSendPublicInvite = async (prospect) => {
    try {
      // Get current user info for the "from" name
      const currentUserEmail = localStorage.getItem("wb.email") || "Your Leader";
      const currentUserName = localStorage.getItem("wb.name") || "Your Leader";
      
      // Send email invitation
      await sendPublicInsightCenterInvite({
        toEmail: prospect.email,
        toName: prospect.firstName || prospect.name,
        fromName: currentUserName
      });
      
      // Log the invite in notes
      await addNoteInternal(prospect.id, [], `✉️ Sent public Insight Center invite email to ${prospect.email}`);
      
      alert(`📧 Invitation sent to ${prospect.name}!\n\nThey'll receive an email with a link to explore Wealth Builders.`);
    } catch (error) {
      console.error("Failed to send public invite:", error);
      alert(`❌ Failed to send invite: ${error.message}`);
    }
  };

  const handleRequestTrainer = async (prospect) => {
    // Open the trainer request modal for user to edit details
    setTrainerRequestFor(prospect);
  };

  const handleSaveTrainerRequest = async (trainerRequestData) => {
    if (!trainerRequestFor) return;

    try {
      const currentUserName = localStorage.getItem('wb.name') || 'Unknown User';
      const currentUserId = localStorage.getItem('wb.userId') || auth.currentUser?.uid;

      // Parse date and time
      const dateTimeStr = `${trainerRequestData.appointmentDate}T${trainerRequestData.appointmentTime || '12:00'}`;
      const appointmentDate = new Date(dateTimeStr);

      // Create appointment with matchUp flag
      const appointmentData = {
        title: `Trainer Request for ${trainerRequestFor.name}`,
        type: trainerRequestData.appointmentType || 'Training',
        quadrant: trainerRequestData.quadrant || 'Not Urgent/Important',
        matchUp: true,
        matchUpStatus: 'pending',
        prospectId: trainerRequestFor.id,
        prospectName: trainerRequestFor.name,
        prospectEmail: trainerRequestFor.email || '',
        prospectPhone: trainerRequestFor.phone || '',
        notes: trainerRequestData.notes || `Training request for ${trainerRequestFor.name}. Timezone: ${trainerRequestData.timezone}. Recruiter: ${trainerRequestFor.recruiter || currentUserName}`,
        duration: parseInt(trainerRequestData.duration) || 60,
        dateTime: Timestamp.fromDate(appointmentDate),
        endTime: Timestamp.fromDate(new Date(appointmentDate.getTime() + (parseInt(trainerRequestData.duration) || 60) * 60 * 1000)),
        userId: currentUserId,
        createdBy: currentUserId,
        createdByName: currentUserName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        completed: false,
        assignedAgent: null,
        requestedBy: currentUserName,
        requestedById: currentUserId,
        timezone: trainerRequestData.timezone,
        appointmentType: trainerRequestData.appointmentType,
        meetingType: trainerRequestData.meetingType,
        profileFlags: trainerRequestData.profileFlags
      };

      const docRef = await addDoc(collection(db, 'appointments'), appointmentData);
      
      // Log the request in notes
      await addNoteInternal(trainerRequestFor.id, [], `✅ Requested trainer via matchup system (ID: ${docRef.id}). Timezone: ${trainerRequestData.timezone}`);
      
      alert(`✅ Successfully added ${trainerRequestFor.name} to matchup system!\n\nTrainer request ID: ${docRef.id}\n\nThe request will appear in the Matchup page for agent assignment.`);
      setTrainerRequestFor(null);
    } catch (error) {
      console.error('Failed to create matchup request:', error);
      alert(`❌ Failed to create matchup request: ${error.message}`);
    }
  };

  const handleAddProductionSave = async (productionData) => {
    if (!addProductionFor) return;

    try {
      // Use the prospect's ID as the tracker document ID
      const trackerId = addProductionFor.id;
      const trackerRef = doc(db, "trackers", trackerId);
      
      // Store production data in the trackers collection under production field
      await setDoc(trackerRef, {
        production: {
          createdFromProspectId: addProductionFor.id,
          prospectName: addProductionFor.name,
          status: productionData.status,
          dateWritten: productionData.dateWritten,
          closureDate: productionData.closureDate || null,
          client: productionData.client,
          agents: {
            agent1: productionData.agent1,
            agent2: productionData.agent2,
            split: productionData.split,
          },
          points: {
            target: parseFloat(productionData.targetPoints) || 0,
            totalPoints: parseFloat(productionData.totalPoints) || 0,
            forty: productionData.pointsA || 0,
            sixty: productionData.pointsB || 0,
          },
          policy: {
            company: productionData.company,
            product: productionData.product,
            otherProduct: productionData.otherProduct || null,
            number: productionData.policyNumber,
          },
          delivery: productionData.delivery,
          trialApp: productionData.trialApp || false,
          chargeback: false,
          chargebackDetails: {
            deposited12Months: false,
            deposited26Months: false,
          },
          notes: productionData.notes || "",
          files: [],
          createdAt: serverTimestamp(),
        },
        updatedAt: serverTimestamp(),
      }, { merge: true });

      alert("Added to Production!");
      setAddProductionFor(null);
    } catch (error) {
      console.error("Failed to save production:", error);
      alert(`Failed to save production: ${error.message}`);
    }
  };

  const handleAddAppointmentSave = async (appointmentData) => {
    if (!addAppointmentFor) return;

    try {
      const appointmentId = uniqueId();
      const appointmentRef = doc(db, "appointments", appointmentId);
      
      await setDoc(appointmentRef, {
        prospectId: addAppointmentFor.id,
        prospectName: addAppointmentFor.name,
        recruiter: addAppointmentFor.recruiter || null,
        leader: addAppointmentFor.leader || null,
        title: appointmentData.title,
        date: appointmentData.date,
        time: appointmentData.time,
        duration: appointmentData.duration,
        location: appointmentData.location,
        meetingType: appointmentData.meetingType,
        notes: appointmentData.notes,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      alert("Appointment added!");
      setAddAppointmentFor(null);
    } catch (error) {
      console.error("Failed to save appointment:", error);
      alert(`Failed to save appointment: ${error.message}`);
    }
  };

  const handleAddAgencyCode = async (formData) => {
    try {
      if (!addingAgencyCodeFor) return;
      
      setInvitingId(addingAgencyCodeFor.id);
      
      // Build comprehensive agent data
      const agentData = {
        ...formData,
        id: addingAgencyCodeFor.id,
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        status: 'invited',
      };

      // Get current recruiter's user ID for tracking
      const recruiterUserId = localStorage.getItem("wb.userId") || auth?.currentUser?.uid;
      console.log("🔍 Current user info:");
      console.log("   wb.userId:", localStorage.getItem("wb.userId"));
      console.log("   auth.currentUser.uid:", auth?.currentUser?.uid);
      console.log("   Using recruiterId:", recruiterUserId);
      console.log("   Prospect ID:", addingAgencyCodeFor.id);
      console.log("   Prospect name:", `${formData.firstName} ${formData.lastName}`);
      
      // IMPORTANT: Update prospect document FIRST with all data before inviteProspect workflow
      // The workflow reads the prospect document and uses these values when creating trackers
      console.log("📝 Updating prospect document with all data BEFORE workflow...");
      await setDoc(
        prospectsRef(addingAgencyCodeFor.id),
        {
          agencyCode: formData.agencyCode,
          amaDate: formData.amaDate,
          code: formData.agencyCode, // For workflow to read
          date: formData.amaDate, // For workflow to read
          poloSize: formData.poloSize,
          spouseName: formData.spouseName,
          spousePhone: formData.spousePhone,
          spousePoloSize: formData.spousePoloSize,
          plan: formData.plan,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          email: formData.email,
          recruiter: formData.recruiter,
          recruiterId: formData.recruiterId || "",
          leader: formData.leader,
          leaderId: formData.leaderId || "",
          level: formData.level,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      console.log("✅ Prospect document updated with all data!");
      
      // NOW call the inviteProspect workflow - it will read the updated prospect doc
      await inviteProspect(addingAgencyCodeFor.id);
      console.log("✅ inviteProspect workflow completed - users and trackers created with proper data");
      
      // Also manually create/ensure Auth user exists (for testing when clicking multiple times)
      try {
        const functions = getFunctions();
        const createAuthUser = httpsCallable(functions, "createAuthUserManually");
        console.log("🔐 Ensuring Auth user exists...");
        const result = await createAuthUser({ userId: addingAgencyCodeFor.id });
        console.log("✅ Auth user result:", result.data);
      } catch (authError) {
        console.log("⚠️ Auth user creation note:", authError.message);
        // Continue anyway - might already exist or be created by Cloud Function
      }

      // Update users collection with additional agent-specific info (polo sizes, spouse, etc.)
      // These fields are not part of the standard workflow but are specific to this system
      
      // Find nearest SMD by traversing up the hierarchy
      const smdInfo = findNearestSMD(formData.recruiterId, allRecruiters);
      console.log("🔍 Found SMD info:", smdInfo);
      
      await setDoc(
        usersRef(addingAgencyCodeFor.id),
        {
          agencyCode: formData.agencyCode,
          amaDate: formData.amaDate,
          poloSize: formData.poloSize,
          spouseName: formData.spouseName,
          spousePhone: formData.spousePhone,
          spousePoloSize: formData.spousePoloSize,
          plan: formData.plan,
          leader: formData.leader,
          leaderId: formData.leaderId || "",
          smdId: smdInfo?.smdId || "",
          smdName: smdInfo?.smdName || "",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      console.log("✅ Users document updated with additional info including SMD");
      
      // No need to update trackers - the workflow already created it with the correct data
      // because we updated the prospect doc first!

      // Send welcome email with profile setup link
      const recruiterName = localStorage.getItem("wb.name") || "Your Leader";
      
      // Get or generate setup token from Firestore
      const userDocRef = usersRef(addingAgencyCodeFor.id);
      const userDocSnap = await getDoc(userDocRef);
      const userDocData = userDocSnap.data();
      
      // Generate new setup token for testing (always fresh)
      const crypto = window.crypto || window.msCrypto;
      const array = new Uint8Array(32);
      crypto.getRandomValues(array);
      const setupToken = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
      
      // Update user with new setup token
      await setDoc(
        userDocRef,
        {
          setupToken: setupToken,
          profileSetupComplete: false,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      
      // Create setup link
      const setupLink = `${window.location.origin}/setup-profile?userId=${addingAgencyCodeFor.id}&token=${setupToken}`;

      // Log in notes
      await addNoteInternal(addingAgencyCodeFor.id, [], `✉️ Sent profile setup email with agency code ${formData.agencyCode}`);

      // Update local state
      setRowLocal(addingAgencyCodeFor.id, (r) => ({
        ...r,
        status: "invited",
        agencyCode: formData.agencyCode,
      }));

      alert(`🎉 ${formData.firstName} ${formData.lastName} has been added as an agent!\n\n📧 They'll receive a welcome email with instructions to set their password and complete their profile.`);
    } catch (error) {
      console.error("Failed to add agent:", error);
      alert(`Failed to add agent: ${error.message}`);
    } finally {
      setInvitingId(null);
      setAddingAgencyCodeFor(null);
    }
  };

  /* 🆕 profile modal routing + data mapping */
  const {
    id: profileId,
    openWithId: openProfile,
    close: closeProfile,
  } = useProfileModalRoute();
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    if (!profileId) return setProfileData(null);
    const row = rows.find((r) => r.id === profileId);
    if (!row) return setProfileData(null);
    const p = row.profile || {};
    setProfileData({
      photoUrl: p.photoUrl,
      calendarUrl: p.calendarUrl,
      amaDate: p.amaDate,
      agencyCode: p.agencyCode,
      name: row.name,
      email: row.email,
      phone: row.phone,
      dob: p.birthday,
      homeAddress: [p.city, p.state].filter(Boolean).join(", "),
      profileCode: p.code,
      keyPlayer: p.keyPlayer,
      level: row.level,
      poloSize: p.poloSize,
      spouseName: p.spouseName,
      spousePhone: p.spousePhone,
      spousePoloSize: p.spousePoloSize,
      recruiter: row.recruiter,
      leader: row.leader,
      nearestSmd: p.nearestSmd,
      plan: p.plan,
      status: row.status || (row.hot ? "HOT" : row.top25 ? "Top 25" : "Active"),
      notes: p.whatTold,
    });
  }, [profileId, rows]);

  /* ---------- Cell renderer ---------- */
  const renderCell = (row, col) => {
    const isEditingProfile = editingProfileId === row.id;
    const draft = isEditingProfile ? editDraft : row;
    const pushDraft = (key, value) => setEditDraft({ ...draft, [key]: value });
    const isNoteFocused = focusedNoteInputId === row.id;

    switch (col.type) {
      case "marker":
        const val = (row.mark || "default").toLowerCase();
        const meta = colorMeta[val] || colorMeta.default;
        return (
          <button
            className="btn small"
            onClick={(e) => openPalette(row, e)}
            title="Highlight row"
            style={{
              padding: 6,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span className="dot" style={{ background: meta.dot }} />
          </button>
        );
      case "text": {
        const isNameLike = ["leader", "recruiter", "name"].includes(col.key);
        if (isNameLike) {
          const nameText = row[col.key] || "";
          return (
            <div className="name-like-cell">
              <button
                className="icon-btn profile-card-btn"
                title="Open profile card"
                onClick={() => openProfile(row.id)}
              >
                🪪
              </button>
              <span className="cell-text" title={nameText}>
                {nameText}
              </span>
            </div>
          );
        }
        return <span className="cell-text">{row[col.key] || ""}</span>;
      }
      case "check":
        const isChecked = !!row[col.key];
        return (
          <button
            className={`fancy-check-btn ${isChecked ? "checked" : ""}`}
            onClick={() =>
              setDoc(
                prospectsRef(row.id),
                {
                  [col.key]: !isChecked,
                  updatedAt: serverTimestamp(),
                },
                { merge: true }
              )
            }
            title={isChecked ? "Uncheck" : "Check"}
          >
            {isChecked ? "✓" : ""}
          </button>
        );
      case "select":
        return (
          <select
            className="cell-select"
            value={row[col.key] || col.options[0]}
            onChange={(e) =>
              setDoc(
                prospectsRef(row.id),
                {
                  [col.key]: e.target.value,
                  updatedAt: serverTimestamp(),
                },
                { merge: true }
              )
            }
          >
            {col.options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        );
      case "profileLine":
        const pline = profileLine(row.profile);
        const flags = row.profile?.flags || {};
        const activeFlagCount = Object.values(flags).filter(Boolean).length;
        
        if (!isEditingProfile)
          return (
            <div className="profile-with-flags">
              <div
                className="profile-line"
                title={pline}
                onClick={() => {
                  setEditingProfileId(row.id);
                  setEditDraft(JSON.parse(JSON.stringify(row)));
                }}
              >
                {pline}
              </div>
              {activeFlagCount > 0 && (
                <div className="profile-flags-inline">
                  {flags.age25Plus && <span className="flag-badge" title="25+ Years Old">25+</span>}
                  {flags.homeowner && <span className="flag-badge" title="Homeowner">🏠</span>}
                  {flags.solidCareer && <span className="flag-badge" title="Solid Career">💼</span>}
                  {flags.income75kPlus && <span className="flag-badge" title="$75k+ Income">💰</span>}
                  {flags.dissatisfied && <span className="flag-badge" title="Dissatisfied">😐</span>}
                  {flags.entrepreneurial && <span className="flag-badge" title="Entrepreneurial">🚀</span>}
                  {flags.spanishPreferred && <span className="flag-badge" title="Spanish Preferred">🇪🇸</span>}
                </div>
              )}
            </div>
          );

        const p = draft.profile || {};
        const age = calculateAge(p.birthday);

        const setP = (k, v) =>
          pushDraft("profile", { ...(draft.profile || {}), [k]: v });

        const handleAgeChange = (e) => {
          const newAge = e.target.value;
          if (newAge && !isNaN(newAge)) {
            const birthYear = new Date().getFullYear() - parseInt(newAge, 10);
            const newBirthday = `${birthYear}-01-01`;
            setP("birthday", newBirthday);
          } else {
            setP("birthday", "");
          }
        };

        return (
          <div className="profile-edit-active">
            <div className="profile-edit-grid-3col">
              <div className="profile-edit-field">
                <label>Relationship</label>
                <input
                  className="chip-input"
                  value={p.howKnown ?? ""}
                  onChange={(e) => setP("howKnown", e.target.value)}
                />
              </div>
              <div className="profile-edit-field">
                <label>Rel. Scale (1-10)</label>
                <input
                  className="chip-input"
                  type="number"
                  min="1"
                  max="10"
                  value={p.relationship ?? ""}
                  onChange={(e) => setP("relationship", e.target.value)}
                />
              </div>
              <div className="profile-edit-field">
                <label>Occupation</label>
                <input
                  className="chip-input"
                  value={p.occupation ?? ""}
                  onChange={(e) => setP("occupation", e.target.value)}
                />
              </div>
              <div className="profile-edit-field">
                <label>Age</label>
                <input
                  className="chip-input"
                  type="number"
                  value={age ?? ""}
                  onChange={handleAgeChange}
                />
              </div>
              <div className="profile-edit-field">
            <label>
              What have you told this person about our company so far?
            </label>
            <input
              className="input"
              value={p.whatTold ?? ""}
              onChange={(e) => setP("whatTold", e.target.value)}
            />
          </div>
          <div className="profile-edit-field">
            <label>How do you know this person?</label>
            <input
              className="input"
              value={p.howKnown ?? ""}
              onChange={(e) => setP("howKnown", e.target.value)}
            />
          </div>
            </div>
            <div className="profile-edit-actions">
              <button className="btn success small" onClick={saveProfileUpdate}>
                Save
              </button>
              <button
                className="btn ghost small"
                onClick={() => setEditingProfileId(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        );
      case "notesInline":
        const notesArr = prospectNotes[row.id] || [];
        const lastNote =
          notesArr.length > 0
            ? notesArr
                .slice()
                .reverse()
                .find((n) => n.text)
            : null;
        
        // Show latest note in the input when not focused, or draft when focused/typing
        const inputValue = isNoteFocused 
          ? (noteDrafts[row.id] || "")
          : (noteDrafts[row.id] || (lastNote?.text || ""));
        
        return (
          <div className="notes-cell-content">
            <div className="notes-inline-add">
              <input
                className={`note-input ${!isNoteFocused && lastNote && !noteDrafts[row.id] ? "showing-existing" : ""}`}
                type="text"
                placeholder="Add a quick note... (Press Enter)"
                value={inputValue}
                onChange={(e) =>
                  setNoteDrafts((d) => ({ ...d, [row.id]: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (noteDrafts[row.id] || "").trim()) {
                    addInlineNote(row);
                  }
                }}
                onFocus={() => {
                  setFocusedNoteInputId(row.id);
                  // Clear the input when user focuses to type new note
                  if (!noteDrafts[row.id]) {
                    setNoteDrafts((d) => ({ ...d, [row.id]: "" }));
                  }
                }}
                onBlur={() => setFocusedNoteInputId(null)}
              />
              <button
                className="notes-openall-emoji"
                onClick={() => setNotesOpenFor(row)}
                title="Open all notes"
                type="button"
              >
                ✏️
              </button>
            </div>
            
            {/* Show note metadata below when showing existing note */}
            {!isNoteFocused && lastNote && !noteDrafts[row.id] && (
              <div className="note-meta-under">
                {lastNote.createdBy || "—"} • {
                  lastNote.createdAtISO
                    ? formatDateTimePretty(lastNote.createdAtISO)
                    : tsToMs(lastNote.createdAt)
                    ? formatDateTimePretty(
                        new Date(tsToMs(lastNote.createdAt)).toISOString()
                      )
                    : ""
                }
              </div>
            )}
          </div>
        );
      case "actionsInline":
        return (
          <div className="actions-inline">
            <button
              className="icon-btn"
              title="Edit Prospect Card"
              onClick={() => setViewingProspect(row)}
            >
              📝
            </button>
            <button
              className="icon-btn"
              title="Log Call"
              onClick={() => setCallLogProspect(row)}
            >
              📞
            </button>
            <button
              className="icon-btn danger"
              title="Delete"
              onClick={() => removeRow(row.id)}
            >
              🗑️
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  // Clear pending filter from localStorage once component mounts
  useEffect(() => {
    try {
      const pending = localStorage.getItem('prospectTracker:pendingFilter');
      if (pending) {
        const filter = JSON.parse(pending);
        if (filter.timestamp > Date.now() - 5000) {
          console.log(`✓ Applied filter from Associate Tracker: ${filter.type} = ${filter.value}`);
        }
        localStorage.removeItem('prospectTracker:pendingFilter');
      }
    } catch (e) {}
  }, []);

  // Reset view handler - clears all filters and URL params
  const handleResetView = () => {
    setLeaderQ("");
    setRecruiterQ("");
    setNameQ("");
    setSearchParams({}); // Clear URL params
    localStorage.removeItem('prospectTracker:pendingFilter'); // Clear any pending filters
  };

  // Check if any filters are active
  const hasActiveFilters = leaderQ || recruiterQ || nameQ;

  const ToolbarRight = (
    <>
      {hasActiveFilters && (
        <button
          className="btn secondary"
          onClick={handleResetView}
          style={{ marginRight: '8px' }}
        >
          🔄 Reset View
        </button>
      )}
      <button
        className="btn primary"
        onClick={fetchContactsVcs}
        disabled={importLoading}
      >
        {importLoading ? "Loading..." : "Import Contacts"}
      </button>
      <button className="btn primary" onClick={() => setAddOpen(true)}>
        + New Prospect
      </button>
    </>
  );
  
  /**
   * Parse VCS (vCard) file and extract contact information
   * @param {string} vcsContent - Raw VCS file content
   * @returns {Array} Array of contact objects with name, email, phone
   */
  const parseVcsFile = (vcsContent) => {
    const contacts = [];
    // Split by BEGIN:VCARD to separate individual contacts
    const vcards = vcsContent.split("BEGIN:VCARD").slice(1);

    vcards.forEach((vcard) => {
      const contact = {
        name: "",
        email: "",
        phone: "",
      };

      // Extract FN (Full Name)
      const fnMatch = vcard.match(/FN:(.+?)(?:\r?\n|$)/);
      if (fnMatch) {
        contact.name = fnMatch[1].trim();
      }

      // Extract N (Name structured)
      const nMatch = vcard.match(/N:(.+?)(?:\r?\n|$)/);
      if (nMatch && !contact.name) {
        const nameParts = nMatch[1].split(";");
        const lastName = nameParts[0] || "";
        const firstName = nameParts[1] || "";
        contact.name = `${firstName} ${lastName}`.trim();
      }

      // Extract EMAIL
      const emailMatch = vcard.match(/EMAIL[^:]*:(.+?)(?:\r?\n|$)/);
      if (emailMatch) {
        contact.email = emailMatch[1].trim();
      }

      // Extract TEL (Phone) - prefer CELL, then HOME, then any TEL
      const cellMatch = vcard.match(/TEL[^:]*TYPE=CELL[^:]*:(.+?)(?:\r?\n|$)/i);
      const homeMatch = vcard.match(/TEL[^:]*TYPE=HOME[^:]*:(.+?)(?:\r?\n|$)/i);
      const anyTelMatch = vcard.match(/TEL[^:]*:(.+?)(?:\r?\n|$)/);

      if (cellMatch) {
        contact.phone = cellMatch[1].trim();
      } else if (homeMatch) {
        contact.phone = homeMatch[1].trim();
      } else if (anyTelMatch) {
        contact.phone = anyTelMatch[1].trim();
      }

      // Only add if we have at least a name
      if (contact.name) {
        contacts.push(contact);
      }
    });

    return contacts;
  };

  async function fetchContactsVcs() {
    try {
      setImportLoading(true);

      // Create a file input element and trigger file selection
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = ".vcf,.vcs,.ics";
      fileInput.style.display = "none";

      fileInput.onchange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
          setImportLoading(false);
          return;
        }

        try {
          // Read the file content
          const fileContent = await file.text();

          // Parse the VCS file
          const contacts = parseVcsFile(fileContent);

          if (contacts.length === 0) {
            alert("No valid contacts found in the VCS file.");
            setImportLoading(false);
            return;
          }

          // Set the contacts and open import modal
          setGoogleContacts(contacts);
          setSelectedContacts(
            contacts.map((c) => ({ ...c, checked: true }))
          );
          setImportOpen(true);

          console.log(`✓ Parsed ${contacts.length} contacts from VCS file`);
        } catch (error) {
          console.error("Error reading/parsing VCS file:", error);
          alert(`Failed to parse VCS file: ${error.message}`);
        } finally {
          setImportLoading(false);
        }
      };

      // Trigger file selection dialog
      fileInput.click();
    } catch (error) {
      console.error("Error in fetchContactsVcs:", error);
      alert(`Failed to import contacts: ${error.message}`);
      setImportLoading(false);
    }
  }
  async function fetchContacts() {
    try {
      setImportLoading(true);
      const result = await signInWithPopup(auth, googleProvider);
      const cred = GoogleAuthProvider.credentialFromResult(result);
      const token =
        cred?.accessToken || result?._tokenResponse?.oauthAccessToken;
      if (!token) throw new Error("No Google OAuth access token.");
      const res = await fetch(
        `https://people.googleapis.com/v1/people/me/connections?personFields=names,emailAddresses,phoneNumbers`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok)
        throw new Error(`People API ${res.status}: ${await res.text()}`);
      const data = await res.json();
      const contacts = (data.connections || []).map((c) => ({
        name: c.names?.[0]?.displayName || "",
        email: c.emailAddresses?.[0]?.value || "",
        phone: c.phoneNumbers?.[0]?.value || "",
      }));
      setGoogleContacts(contacts);
      setSelectedContacts(contacts.map((c) => ({ ...c, checked: true })));
      setImportOpen(true);
    } catch (err) {
      console.error(err);
      alert(
        `Could not fetch Google contacts.\n\nDetails: ${
          err?.message || String(err)
        }`
      );
    } finally {
      setImportLoading(false);
    }
  }

  const uniqueRecruiters = useMemo(
    () => [
      ...new Set(
        rows
          .map((r) => r.recruiter)
          .filter(Boolean)
          .sort()
      ),
    ],
    [rows]
  );

  const currentNotesForOpenRow = useMemo(() => {
    if (!notesOpenFor) return [];
    return prospectNotes[notesOpenFor.id] || [];
  }, [notesOpenFor, prospectNotes]);

  const sortedModalNotes = useMemo(() => {
    const copy = [...currentNotesForOpenRow];
    copy.sort((a, b) => tsToMs(a.createdAt) - tsToMs(b.createdAt));
    return copy;
  }, [currentNotesForOpenRow]);

  return (
    <TooltipProvider>
      <TrackerShell title="Prospect Tracker" right={ToolbarRight}>
        <ResizableGrid
          columns={COLS}
          rows={sorted}
          loading={loading}
          emptyText="No prospects found."
          renderCell={renderCell}
          rowKey={(r) => r.id}
          lockableLeftCount={3}
          rowStyle={rowVars}
          sortKey={effectiveSortKey}
          sortDir={effectiveSortDir}
          onSort={handleHeaderSort}
          onScroll={loadMore}
          loadingMore={loadingMore}
          renderHeaderSearch={(col) => {
            if (col.key === "leader")
              return (
                <input
                  className="mini-search"
                  placeholder="Search Leader…"
                  value={leaderQ}
                  onChange={(e) => setLeaderQ(e.target.value)}
                />
              );
            if (col.key === "recruiter")
              return (
                <input
                  className="mini-search"
                  placeholder="Search Recruiter…"
                  value={recruiterQ}
                  onChange={(e) => setRecruiterQ(e.target.value)}
                />
              );
            if (col.key === "name")
              return (
                <input
                  className="mini-search"
                  placeholder="Search Name…"
                  value={nameQ}
                  onChange={(e) => setNameQ(e.target.value)}
                />
              );
            return null;
          }}
        />

        <ProspectCardModal
          open={addOpen || !!viewingProspect}
          onClose={() => {
            setAddOpen(false);
            setViewingProspect(null);
          }}
          onSave={handleSaveProspect}
          defaultRecruiter={recruiterName}
          defaultRecruiterId={allRecruiters.find(r => r.name === recruiterName || r.email === auth?.currentUser?.email)?.id || auth?.currentUser?.uid || ""}
          existingProspect={viewingProspect}
          recruiters={allRecruiters}
        />

        <CallLogModal
          prospect={callLogProspect}
          onClose={() => setCallLogProspect(null)}
          onSaveLog={handleSaveCallLog}
          onInvite={handleSendPublicInvite}
          onAddAgencyCode={setAddingAgencyCodeFor}
          onRequestTrainer={handleRequestTrainer}
          onAddAppointment={setAddAppointmentFor}
          onAddProduction={setAddProductionFor}
          isAlreadyInvited={callLogProspect?.status === "invited"}
          hideRestrictedActions={isNewAgent}
          hideAddAgencyCode={isAgent}
        />

        <AddAgencyCodeModal
          prospect={addingAgencyCodeFor}
          onClose={() => setAddingAgencyCodeFor(null)}
          onSubmit={handleAddAgencyCode}
        />

        <AddProductionModal
          open={!!addProductionFor}
          prospect={addProductionFor}
          onClose={() => setAddProductionFor(null)}
          onSave={handleAddProductionSave}
        />

        <AddAppointmentModal
          open={!!addAppointmentFor}
          prospect={addAppointmentFor}
          onClose={() => setAddAppointmentFor(null)}
          onSave={handleAddAppointmentSave}
        />

        <TrainerRequestModal
          open={!!trainerRequestFor}
          prospect={trainerRequestFor}
          onClose={() => setTrainerRequestFor(null)}
          onSave={handleSaveTrainerRequest}
        />

        {pal && (
          <div
            ref={palRef}
            className="palette-pop"
            style={{ left: pal.x, top: pal.y }}
            role="menu"
          >
            {["default", "client", "recruit", "both"].map((k) => (
              <div
                key={k}
                className="pal-item"
                onClick={() => setMark(pal.row, k)}
              >
                <span
                  className="dot"
                  style={{ background: (colorMeta[k] || {}).dot }}
                />
                <span>{(colorMeta[k] || {}).label}</span>
              </div>
            ))}
          </div>
        )}

        <Modal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          title="Import Contacts"
          wide
        >
          <div className="import-list">
            {googleContacts.length === 0 && <div>No contacts found.</div>}
            {googleContacts.map((c, i) => (
              <label
                key={i}
                className="check-wrap"
                style={{ display: "flex", gap: 12 }}
              >
                <input
                  type="checkbox"
                  checked={!!selectedContacts[i]?.checked}
                  onChange={(e) => {
                    const next = [...selectedContacts];
                    next[i] = { ...next[i], checked: e.target.checked };
                    setSelectedContacts(next);
                  }}
                />
                <span>
                  <b>{c.name || "—"}</b> — {c.email || "No email"} —{" "}
                  {c.phone || "No phone"}
                </span>
              </label>
            ))}
          </div>
          <div
            style={{
              marginTop: 16,
              textAlign: "right",
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <button className="btn ghost" onClick={() => setImportOpen(false)}>
              Cancel
            </button>
            <button
              className="btn primary"
              onClick={async () => {
                const toImport = selectedContacts.filter((c) => c.checked);
                // Find current user's ID from allRecruiters based on recruiterName
                const currentRecruiter = allRecruiters.find(
                  r => r.name === recruiterName || r.email === auth?.currentUser?.email
                );
                const currentRecruiterId = currentRecruiter?.id || auth?.currentUser?.uid || "";
                
                for (const c of toImport) {
                  try {
                    const prospectInput = {
                      firstName: c.name.split(" ")[0] || "",
                      lastName: c.name.split(" ").slice(1).join(" ") || "",
                      email: c.email || "",
                      phone: c.phone || "",
                      leader: "",
                      level: "",
                      recruiter: recruiterName,
                      recruiterId: currentRecruiterId,
                      managerEmail: null,
                      code: "",
                      date: null,
                    };

                    await addProspect(prospectInput);
                  } catch (error) {
                    console.error("Error importing contact:", error);
                  }
                }
                alert(`${toImport.length} contacts imported.`);
                setImportOpen(false);
              }}
              disabled={!selectedContacts.some((c) => c.checked)}
            >
              Import Selected
            </button>
          </div>
        </Modal>

        {notesOpenFor && (
          <div className="modal-backdrop" onClick={() => setNotesOpenFor(null)}>
            <div
              className="modal-card"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
            >
              <div className="modal-head">
                <h3>Notes — {notesOpenFor.name}</h3>
                <button
                  className="btn ghost"
                  onClick={() => setNotesOpenFor(null)}
                >
                  ✕
                </button>
              </div>
              <div className="notes-list-modal">
                {sortedModalNotes.length === 0 && (
                  <div className="empty-notes">No notes yet.</div>
                )}
                {sortedModalNotes.map((n) => (
                  <div key={n.id} className="note-row-modal">
                    <div className="note-text-modal">{n.text}</div>
                    <div className="note-meta-modal">
                      {n.createdBy || "—"} •{" "}
                      {n.createdAtISO
                        ? formatDateTimeISO(n.createdAtISO)
                        : tsToMs(n.createdAt)
                        ? formatDateTimeISO(
                            new Date(tsToMs(n.createdAt)).toISOString()
                          )
                        : ""}
                    </div>
                    <button
                      className="note-delete-modal"
                      onClick={() => deleteNote(notesOpenFor, n.id)}
                      disabled={noteDeletingId === n.id}
                      title="Delete note"
                    >
                      {noteDeletingId === n.id ? "…" : "Delete"}
                    </button>
                  </div>
                ))}
              </div>
              <div className="notes-modal-add">
                <textarea
                  className="input"
                  rows="2"
                  placeholder="Add a new note..."
                  value={noteDrafts["modal"] || ""}
                  onChange={(e) =>
                    setNoteDrafts((d) => ({ ...d, modal: e.target.value }))
                  }
                ></textarea>
                <button
                  className="btn primary"
                  onClick={() => {
                    const text = (noteDrafts["modal"] || "").trim();
                    if (text) {
                      addNoteInternal(
                        notesOpenFor.id,
                        [],
                        text,
                        notesOpenFor.email
                      );
                      setNoteDrafts((d) => ({ ...d, modal: "" }));
                    }
                  }}
                >
                  Add Note
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 🆕 Mount the profile card modal */}
        <UserProfileCard
          open={!!profileId && !!profileData}
          onClose={closeProfile}
          person={profileData || {}}
        />

        <style>{`
        .cell-text, .profile-line { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; }
        .actions-inline { display: flex; gap: 8px; align-items: center; flex-wrap: nowrap; }
        .modal-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.6);display:flex;align-items:center;justify-content:center;z-index:1000}
        .modal-card{background:#1f2128;color:#fff;border:1px solid #3a3f4a;border-radius:16px;box-shadow:0 14px 40px rgba(0,0,0,.45);padding:16px 16px 12px;max-height:92vh;overflow:auto;width:min(860px,94vw)}
        .modal-card-wide{width:min(980px,96vw)}
        .modal-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:8px}
        .modal-body{display:block}
        .row{display:flex;gap:12px;align-items:flex-start}
        .row.two>*{flex:1}
        .stack{display:grid;gap:6px}
        .subhead{font-weight:800;opacity:.9;margin-top:4px; text-align: left;}
        .input{width: 100%; box-sizing: border-box; background:rgba(255,255,255,.06);color:#fff;border:1px solid rgba(255,255,255,.15);border-radius:10px;padding:10px 12px;outline:none}
        .input:focus{border-color:rgba(255,215,0,.55);box-shadow:0 0 0 2px rgba(255,215,0,.12) inset}
        .btn{background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.18);color:#fff;border-radius:10px;padding:8px 12px;cursor:pointer}
        .btn.primary{background:rgba(255,215,0,.2);color:#ffd700;border-color:rgba(255,215,0,.35)}
        .btn.ghost{background:transparent;border-color:rgba(255,255,255,.25)}
        
        /* Force tooltip z-index above everything in ProspectTracker */
        [data-radix-tooltip-content] { z-index: 999999 !important; position: fixed !important; }
        [data-radix-tooltip-root] { z-index: 999999 !important; }
        [data-radix-portal] { z-index: 999999 !important; }
        .btn.success{background:rgba(46,204,113,.25);border-color:rgba(46,204,113,.5)}
        .btn.danger{background:rgba(239,83,80,.25);border-color:rgba(239,83,80,.55)}
        .btn.small{padding:6px 10px;font-size:13px}
        /* Fancy Checkmark Button */
        .fancy-check-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 2px solid rgba(255,255,255,.25);
          background: rgba(255,255,255,.06);
          color: transparent;
          font-size: 18px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .fancy-check-btn:hover {
          background: rgba(255,255,255,.12);
          border-color: rgba(255,215,0,.35);
        }
        .fancy-check-btn.checked {
          background: linear-gradient(135deg, rgba(255,215,0,.25), rgba(255,215,0,.15));
          border-color: rgba(255,215,0,.6);
          color: #ffd700;
          box-shadow: 0 0 12px rgba(255,215,0,.3);
        }
        .fancy-check-btn.checked:hover {
          background: linear-gradient(135deg, rgba(255,215,0,.35), rgba(255,215,0,.25));
          box-shadow: 0 0 16px rgba(255,215,0,.4);
        }
        .cell-input,.cell-select{width:100%;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.2);color:#fff;border-radius:8px;padding:6px 8px}
        .cell-select option{background:#fff !important;color:#111 !important}
        .check-wrap{display:inline-flex;align-items:center;gap:8px; white-space: nowrap; text-align: left;}
        .check-wrap.small { font-size: 14px; }
        .check-wrap input{transform:translateY(1px)}
        .profile-with-flags { display: flex; flex-direction: column; gap: 6px; width: 100%; align-items: flex-start; }
        .profile-line{opacity:.95;padding:6px 10px;border-radius:10px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.14); cursor: pointer; width: 100%; text-align: left;}
        .profile-line:hover { background: rgba(255,255,255,.08); }
        .profile-flags-inline { display: flex; flex-wrap: wrap; gap: 4px; padding: 0 4px; }
        .flag-badge { 
          display: inline-flex; 
          align-items: center; 
          justify-content: center;
          padding: 2px 8px; 
          border-radius: 12px; 
          background: rgba(255,215,0,.15); 
          border: 1px solid rgba(255,215,0,.4);
          color: #ffd700;
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
          cursor: help;
        }
        .profile-edit-active { overflow: visible; display: flex; flex-direction: column; gap: 8px; width: 100%; align-items: flex-start; }
        .profile-edit-grid-3col { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px 12px; width: 100%;}
        .profile-edit-field { display: flex; flex-direction: column; gap: 4px; }
        .profile-edit-field label { font-size: 11px; opacity: 0.8; text-align: left; }
        .profile-edit-checkbox { align-self: end; padding-bottom: 6px; }
        .profile-edit-actions { display: flex; gap: 8px; }
        .chip-input{width:100%;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.18);border-radius:9px;padding:6px 8px;color:#fff}
        .notes-cell-content { display: flex; flex-direction: column; gap: 4px; width: 100%; align-items: flex-start;}
        .notes-inline-add { display: flex; align-items: center; gap: 6px; width: 100%; }
        .note-input { 
          flex: 1 1 auto; 
          min-width: 120px; 
          background: rgba(255,255,255,.08); 
          color: #fff; 
          border: 1px solid rgba(255,255,255,.2); 
          border-radius: 8px; 
          padding: 6px 10px; 
          transition: all 0.2s ease;
        }
        .note-input.showing-existing {
          background: rgba(255,215,0,.08);
          border-left: 3px solid rgba(255,215,0,.4);
          padding-left: 8px;
          font-style: italic;
          opacity: 0.95;
        }
        .notes-openall-emoji { background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.2); border-radius: 8px; padding: 6px 8px; cursor: pointer; }
        .note-meta-under { font-size: 11px; opacity: .8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .import-list{display:grid;gap:8px;max-height:50vh;overflow:auto;padding-right:4px}
        /* Recruiter Search Modal */
        .recruiter-search .input { margin-bottom: 12px; }
        .recruiter-list { max-height: 40vh; overflow-y: auto; }
        .recruiter-item { padding: 10px 12px; border-radius: 8px; cursor: pointer; }
        .recruiter-item:hover { background: rgba(255,255,255,0.1); }
        /* Notes Modal */
        .notes-list-modal { max-height: 40vh; overflow: auto; background: rgba(255,255,255,.04); border: 1px solid rgba(255,255,255,.08); border-radius: 12px; padding: 8px; margin-bottom: 12px;}
        .empty-notes { opacity: .7; text-align: center; padding: 18px 8px; }
        .note-row-modal { display: grid; grid-template-columns: 1fr auto auto; gap: 10px; align-items: center; padding: 8px; border-radius: 10px; }
        .note-row-modal:not(:last-child) { border-bottom: 1px dashed rgba(255,255,255,.08); }
        .note-text-modal { white-space: pre-wrap; }
        .note-meta-modal { opacity: .8; font-size: 12px; }
        .note-delete-modal { background: transparent; border: 1px dashed rgba(255,100,100,.4); color: rgba(255,100,100,.9); border-radius: 8px; padding: 4px 8px; cursor: pointer; }
        .notes-modal-add { display: flex; flex-direction: column; gap: 8px; }
      `}</style>
      </TrackerShell>
    </TooltipProvider>
  );
}
